/**
 * Files & Links — Phase 1.
 *
 * Files and folders persist via the backend (see `filesQuery` and
 * `foldersQuery`). External links and starring are hidden until the
 * matching endpoints ship — see the inline comments where the UI
 * would re-appear.
 *
 * Endpoints in use:
 *   GET    /files              — list + ?folderId + ?search
 *   POST   /files              — multipart upload
 *   PATCH  /files/:id          — rename / re-tag / move folder
 *   DELETE /files/:id          — hard delete (S3 + Mongo)
 *   POST   /files/:id/duplicate
 *   GET    /files/:id/download — signed URL (60s TTL)
 *   GET    /files/storage      — totals + per-kind breakdown
 *   GET    /file-folders       — list with fileCount
 *   POST   /file-folders       — create
 *   PATCH  /file-folders/:id   — rename
 *   DELETE /file-folders/:id   — delete (blocked if non-empty)
 */
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { api, extractApiError, isPlanLimitError } from '../../lib/api'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { FilterRow } from '../../components/filters/FilterRow'
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Calendar,
  ChevronDown,
  Copy,
  CornerDownRight,
  Download,
  ExternalLink as ExternalLinkIcon,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Image as ImageIcon,
  Layers,
  Link2,
  MoreVertical,
  Pencil,
  Presentation,
  Filter as FilterIcon,
  Tag,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { Avatar } from '../../components/Avatar'
import { Breadcrumb } from '../../components/Breadcrumb'
import VisibilityPills from '../../components/VisibilityPills'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { formatDateTime, formatNumber } from '../../lib/format'
import { copyText } from '../../lib/clipboard'
import { useMaxUploadBytes } from '../../lib/useMaxUploadBytes'
import { usePageSize } from '../../lib/display-prefs'

// Files and folders both come from the API (see `filesQuery` and
// `foldersQuery` below).
const dayMs = 24 * 60 * 60 * 1000

// Visual config for the storage buckets the API returns. Order matches
// the rendering order on the card. We now only accept business
// documents and images on upload (audio / video / archives are
// rejected at the API layer), so the two unused buckets are hidden —
// they'd always render as `0 B` and confuse the panel. Pre-existing
// rows from before the allowlist will still be classified into the
// dropped buckets server-side, but their totals fold under the
// catch-all `documents` row visually so nothing goes "missing" from
// the user's perspective.
const STORAGE_BUCKETS = [
  { key: 'documents', kind: 'Documents', icon: FileText, tone: 'sky' },
  { key: 'images', kind: 'Images', icon: ImageIcon, tone: 'violet' },
]

/**
 * Client-side mirror of the server's upload allowlist. Same shape as
 * `ALLOWED_UPLOAD_EXTENSIONS` in `flowdesk-api/src/modules/files/files.service.js`
 * so a file that would be rejected server-side gets a friendly toast
 * instantly rather than the user waiting for an HTTP round trip and a
 * generic "Bad Request". The server stays the source of truth — this
 * is purely a UX shortcut.
 */
const ALLOWED_UPLOAD_EXTENSIONS = new Set([
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
])

/** `accept` attribute string for the upload `<input type="file">`. The
 *  OS file picker uses this to filter what the user can pick before
 *  they even submit. Built once at module-load. */
const UPLOAD_ACCEPT_ATTR = [...ALLOWED_UPLOAD_EXTENSIONS]
  .map((ext) => `.${ext}`)
  .join(',')

/** Human-readable comma-separated extension list used in the upload
 *  dialog helper text AND the toast error on rejection. Sharing one
 *  string means the wording can never drift between "what we say is
 *  allowed" and "what's actually allowed".
 *  Produces: ".pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt, .csv,
 *  .jpg, .jpeg, .png, .gif, .webp, .svg" */
const UPLOAD_EXTENSIONS_TEXT = [...ALLOWED_UPLOAD_EXTENSIONS]
  .map((ext) => `.${ext}`)
  .join(', ')


/** Returns true when the file's extension is in the allowlist. Used
 *  for the client-side pre-check inside the upload dialog. */
function isAllowedUploadFile(file) {
  if (!file?.name) return false
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return ALLOWED_UPLOAD_EXTENSIONS.has(ext)
}

// ─── Format helpers ─────────────────────────────────────────────────────────
function fmtSize(bytes) {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${formatNumber(bytes, { useGrouping: false })} B`
  if (bytes < 1024 * 1024) return `${formatNumber(bytes / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })} KB`
  if (bytes < 1024 * 1024 * 1024) return `${formatNumber(bytes / 1024 / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })} MB`
  return `${formatNumber(bytes / 1024 / 1024 / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })} GB`
}

/**
 * Client-side URL validity check for the Add/Edit Link dialogs. Mirrors
 * the backend `parseUrl` in `externalLinks.service.js` so the inline
 * error matches what the server would reject — the form no longer lets
 * garbage like "not-a-valid-url-12345" through (which `new URL()` alone
 * accepts as a single-label host, saving a broken link). Auto-prepends
 * https:// for bare domains, requires http(s), and demands a real domain
 * shape (dotted host + alphabetic TLD) unless it's localhost or an IP.
 */
function isLikelyValidUrl(raw) {
  const trimmed = String(raw ?? '').trim()
  if (!trimmed) return false
  const normalised = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  let u
  try {
    u = new URL(normalised)
  } catch {
    return false
  }
  if (!['http:', 'https:'].includes(u.protocol)) return false
  const hostname = u.hostname.toLowerCase()
  if (hostname === 'localhost') return true
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return true // IPv4
  if (hostname.includes(':')) return true // IPv6 literal
  return /\.[a-z]{2,}$/.test(hostname) // dotted domain w/ alpha TLD
}

// Tag limits — mirror the backend (`normaliseTags` in files.service.js).
const MAX_TAG_LENGTH = 32
const MAX_TAGS = 20

/**
 * Parse a comma-separated tag string into normalised tags plus a
 * validation error (or null). Mirrors the backend's strict rules so the
 * inline error matches what the server would reject — an over-long tag
 * no longer slips through and reads as a false "Tags updated." success.
 */
function parseTags(raw) {
  const tags = String(raw ?? '')
    .split(',')
    .map((t) => t.trim().toLowerCase().replace(/\s+/g, '-'))
    .filter((t) => t.length > 0)
  let error = null
  if (tags.some((t) => t.length > MAX_TAG_LENGTH)) {
    error = 'Tag name cannot exceed 32 characters.'
  } else if (new Set(tags).size > MAX_TAGS) {
    error = 'You can add a maximum of 20 tags.'
  }
  return { tags, error }
}

/**
 * Tag chips render with a leading "#" (e.g. "#star"), so a user searching by
 * a tag they can see naturally types "#star" — but tags are stored and
 * matched without it, so both the server regex and the client filter miss.
 * Strip a leading "#" from the search term so the visible tag text matches.
 * Only the leading "#" is removed, so a filename containing "#" mid-string
 * stays searchable literally.
 */
function stripLeadingHash(raw) {
  return String(raw ?? '').replace(/^#+\s*/, '')
}

/**
 * Force a file download from a presigned URL via a hidden anchor click.
 * The backend issues the URL with `Content-Disposition: attachment`, so
 * the browser saves the file instead of opening it inline. This is used
 * INSTEAD of `window.open` because the browser popup-blocks all but the
 * first `window.open` when several files are downloaded at once — which
 * is exactly why "the first file opened in a new tab and the rest never
 * downloaded."
 */
function triggerFileDownload(url, filename) {
  const a = document.createElement('a')
  a.href = url
  // `download` is a best-effort hint (ignored cross-origin); the
  // server-side attachment disposition is what actually forces the save.
  if (filename) a.download = filename
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/** Small delay so a burst of programmatic downloads isn't dropped /
 *  collapsed by the browser. */
function downloadDelay() {
  return new Promise((resolve) => {
    setTimeout(resolve, 400)
  })
}

// Approximate menu heights so we can decide whether to drop a portal
// menu downward (default) or flip it above the anchor button when
// there isn't enough room below the fold. The actual rendered height
// will vary by 1–2 px from these constants — that's fine, the goal is
// just to keep the menu fully visible.
const ROW_MENU_HEIGHT = 288 // 7 items + divider + Delete
const FOLDER_MENU_HEIGHT = 88 // 2 items + divider

/**
 * Position a portal menu relative to an anchor button. Returns an
 * object with `top` *or* `bottom` plus `right`, used directly as
 * inline style on the portalled menu. Flips upward when the menu
 * would overflow the viewport's bottom edge.
 */
function computeMenuRect(anchorEl, menuHeight) {
  const rect = anchorEl.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const right = Math.max(8, window.innerWidth - rect.right)
  if (spaceBelow >= menuHeight + 8) {
    // Plenty of room — open below the button as normal.
    return { top: rect.bottom + 4, right }
  }
  // Not enough room below. Flip above the button so the menu is
  // anchored to its bottom edge instead.
  return { bottom: window.innerHeight - rect.top + 4, right }
}

const TYPE_META = {
  pdf: { label: 'PDF', icon: FileText, tone: 'rose' },
  spreadsheet: { label: 'Spreadsheet', icon: FileSpreadsheet, tone: 'emerald' },
  slides: { label: 'Slides', icon: Presentation, tone: 'amber' },
  image: { label: 'Image', icon: ImageIcon, tone: 'violet' },
  archive: { label: 'Archive', icon: Archive, tone: 'sky' },
  document: { label: 'Document', icon: FileText, tone: 'sky' },
  other: { label: 'File', icon: FileIcon, tone: 'slate' },
}

// Visual metadata for external link providers. The `type` field comes
// from the backend `externalLinks.service.classifyDomain` so the two
// stay in sync — when adding a provider, update both files.
const LINK_TYPE_META = {
  google_doc: { label: 'Google Doc', icon: FileText, tone: 'sky' },
  google_sheet: { label: 'Google Sheet', icon: FileSpreadsheet, tone: 'emerald' },
  google_slide: { label: 'Google Slides', icon: Presentation, tone: 'amber' },
  google_drive: { label: 'Google Drive', icon: Folder, tone: 'sky' },
  notion: { label: 'Notion', icon: FileText, tone: 'slate' },
  figma: { label: 'Figma', icon: ImageIcon, tone: 'violet' },
  airtable: { label: 'Airtable', icon: FileSpreadsheet, tone: 'amber' },
  dropbox: { label: 'Dropbox', icon: Folder, tone: 'sky' },
  onedrive: { label: 'OneDrive', icon: Folder, tone: 'sky' },
  generic: { label: 'Link', icon: ExternalLinkIcon, tone: 'slate' },
}

const TONE_BG = {
  rose: 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border-[color:var(--color-danger-border)]',
  emerald: 'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border-[color:var(--color-success-border)]',
  amber: 'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border-[color:var(--color-warning-border)]',
  violet: 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]',
  sky: 'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border-[color:var(--color-info-border)]',
  slate: 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]',
}

// The type-filter set MUST mirror what the upload allowlist actually
// accepts (see `assertAllowedUploadType` / `ALLOWED_UPLOAD_EXTENSIONS`
// in the API's files.service.js). Archives, audio, and video are
// rejected on upload, so a file can NEVER be classified as those —
// offering an "Archives" chip (as an older build did) was a dead
// filter that always returned "No files match", which is exactly the
// defect a tester reported. Conversely, `document` (doc/docx/txt) IS
// uploadable, so it needs its own chip — without it, every Word/text
// file was invisible to the type filter. Keep this list == the set of
// `type` values `normaliseFile` can actually produce for live uploads.
const TYPE_FILTERS = [
  { value: 'all', label: 'All files' },
  { value: 'document', label: 'Documents' },
  { value: 'pdf', label: 'PDFs' },
  { value: 'spreadsheet', label: 'Spreadsheets' },
  { value: 'slides', label: 'Slides' },
  { value: 'image', label: 'Images' },
]

const DATE_FILTERS = [
  { value: 'all', label: 'Any time', days: null },
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
]

// Default order: newest first, matching the backend's `createdAt: -1`.
// Users can re-sort by clicking any column header (see `SortableTh`),
// which drives the `sort` state through the client-side filter pipeline
// below. Sort values are `<key>-<asc|desc>`; every key here must have a
// matching case in the comparator inside `filteredFiles`.
const DEFAULT_SORT = 'updated-desc'

// The "Recent" smart-folder chip was removed on user request: its
// filter was a no-op (`() => true`) so the chip labelled "Recent N"
// was actually showing every file, which read as misleading rather
// than helpful. The Starred / Shared / Trash chips that were
// planned to join it never landed because the backend support
// isn't there, so the whole infrastructure is gone for now. If
// any of them get added later, restore the chip row from git
// history.

// ─── Page ───────────────────────────────────────────────────────────────────

/** Map an API row to the shape the rest of the page already expects.
 *  The API returns `sizeBytes` / `createdAt` / `ownerId` / `ownerName`;
 *  this adapter renames those + classifies MIME into a UI bucket so
 *  every consumer downstream has a flat row to work with. */
function normaliseFile(row, currentUserId) {
  const mime = String(row.mimeType ?? '').toLowerCase()
  const name = row.name ?? ''
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  let type = 'other'
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
    type = 'image'
  } else if (mime === 'application/pdf' || ext === 'pdf') {
    type = 'pdf'
  } else if (mime.includes('spreadsheet') || ['xlsx', 'xls', 'csv'].includes(ext)) {
    type = 'spreadsheet'
  } else if (mime.includes('presentation') || ['pptx', 'ppt', 'key'].includes(ext)) {
    type = 'slides'
  } else if (mime.includes('zip') || mime.includes('compressed') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    type = 'archive'
  } else if (mime.startsWith('video/') || mime.startsWith('audio/')) {
    type = 'media'
  } else if (mime.startsWith('text/') || mime.includes('word') || mime.includes('document')) {
    type = 'document'
  }
  const ownerName = row.ownerName?.trim() || 'Unknown user'
  const isOwner = currentUserId && row.ownerId === currentUserId
  return {
    id: row.id,
    name,
    type,
    mimeType: row.mimeType,
    size: row.sizeBytes ?? 0,
    ownerId: row.ownerId ?? null,
    ownerName,
    // Task(s) this file is attached to ({ id, name }[]) — shown as a muted
    // sub-line under the file name so users see which task/subtask a file
    // belongs to. Empty when the file isn't attached to any task.
    tasks: Array.isArray(row.tasks) ? row.tasks : [],
    // The backend now returns the owner's avatarUrl alongside their
    // name. Used by the shared <Avatar /> component on the Files
    // table so a real face shows instead of initials when the
    // owner has uploaded a profile picture.
    ownerAvatarUrl: row.ownerAvatarUrl ?? null,
    isOwner: Boolean(isOwner),
    ownerInitials: ownerName
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?',
    folderId: row.folderId ?? null,
    folderName: row.folderName ?? '—',
    // Carry the server's visibility through to the row object. Without
    // this the Files table's Visibility column (and the inline private
    // badge before it) always saw `undefined` and rendered every file
    // as "Public" — including genuinely private ones. The API already
    // returns this on every file row (files.service.serialise).
    visibility: row.visibility ?? 'public',
    updatedAt: row.updatedAt ?? row.createdAt,
    tags: row.tags ?? [],
    version: row.version ?? 1,
    // Append-only version history (newest first), each entry
    // { version, sizeBytes, mimeType, uploadedByName, uploadedAt }.
    // Drives the drawer's Version history list + per-version download.
    versions: Array.isArray(row.versions) ? row.versions : [],
  }
}

export function FilesPage() {
  const qc = useQueryClient()
  // Current user — used to flag the rows you own with a small "(you)"
  // hint while still showing the real owner name for everyone else.
  const currentUser = useAuthStore((s) => s.user)
  const currentUserId = currentUser?.id ?? null
  // [External Collaboration] Externals get the SAME Files & Links operations an
  // internal member would, scoped to their granted Spaces — the server is the
  // single authz engine (grant = WHERE, owner = WHAT), so per-file/link controls
  // stay visible and are enforced server-side. We only hide COMPANY-WIDE controls
  // the server blocks for externals: folder management (folders are tenant-wide),
  // gated via `canManage` on the Folders card below.
  const isExternalMember = currentUser?.membershipType === 'external'

  // Folder query — feeds the cards row, the upload picker, and the
  // sidebar filter chip. We fetch this BEFORE files so the picker is
  // populated by the time the upload modal opens.
  const foldersQuery = useQuery({
    queryKey: ['file-folders'],
    queryFn: async () => {
      const res = await api.get('/file-folders')
      return res.data?.data ?? []
    },
  })
  const folders = foldersQuery.data ?? []

  // The selected folder filter — `null` means "all folders". Clicking
  // a folder card sets this and the files query refetches with the
  // matching `?folderId=` filter.
  const [selectedFolderId, setSelectedFolderId] = useState(null)

  const [search, setSearch] = useState('')
  // Debounce the input value so we don't fire one query per keystroke.
  // 250 ms feels responsive but coalesces a typed-out word into a
  // single request.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(stripLeadingHash(search.trim())),
      250,
    )
    return () => clearTimeout(t)
  }, [search])

  const filesQuery = useQuery({
    // Search + folder are part of the cache key so React Query
    // remembers each filter state independently. `limit: 500` is the
    // backend's max — server-side search needs to see beyond the
    // default 100-row page.
    queryKey: [
      'files',
      {
        folderId: selectedFolderId,
        search: debouncedSearch || undefined,
        me: currentUserId,
      },
    ],
    queryFn: async () => {
      const params = { limit: 500 }
      if (selectedFolderId) params.folderId = selectedFolderId
      if (debouncedSearch) params.search = debouncedSearch
      const res = await api.get('/files', { params })
      return (res.data?.data ?? []).map((row) =>
        normaliseFile(row, currentUserId),
      )
    },
  })

  // Storage summary — totals + per-kind breakdown for the sidebar
  // card. Refreshes whenever a file is uploaded / deleted /
  // duplicated (the mutations invalidate this key).
  const storageQuery = useQuery({
    queryKey: ['files', 'storage'],
    queryFn: async () => {
      const res = await api.get('/files/storage')
      return res.data
    },
  })

  // Per-user storage breakdown — admin-panel observability, visible to
  // Company Admins AND Sub Admins (who share the admin panel; see the
  // `/overview` route which grants the same parity). The endpoint is
  // gated server-side to the same two roles; skipping the fetch on
  // regular-user sessions avoids a guaranteed 403 in their console and
  // keeps the sidebar visibly free of admin-only cards.
  const actorRole = useAuthStore((s) => s.user?.role)
  const isAdminViewer =
    actorRole === 'company_admin' || actorRole === 'sub_admin'
  // Only roles that can actually reach the Billing page may act on the
  // "Upgrade Storage" CTA — storage is a tenant-plan setting, not a
  // per-user one. The /billing route is `CompanyAdminRoute` (Company Admin
  // ONLY — Sub Admins are redirected to the Dashboard), so ONLY Company
  // Admins get the working button. Sub Admins and regular users (manager /
  // team_leader / employee) can't change the plan, so they get a
  // "contact your admin" note instead of a button that would just dump them
  // on the Dashboard.
  const navigate = useNavigate()
  const canUpgradeStorage = actorRole === 'company_admin'
  const storageByUserQuery = useQuery({
    queryKey: ['admin', 'storage', 'by-user'],
    enabled: isAdminViewer,
    queryFn: async () => {
      const res = await api.get('/admin/storage/by-user')
      return res.data
    },
  })
  const files = filesQuery.data ?? []
  const [typeFilter, setTypeFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('all')
  // Client-side pagination over the already-fetched (≤500) file rows —
  // mirrors the shared Pagination bar used on Members / Workspaces / Audit
  // Log. Server offset paging isn't available (the /files endpoint takes
  // only `limit`), and the full filtered set is already in memory, so we
  // slice locally. 25/page matches the component's first canonical option.
  const [filesPage, setFilesPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [filesLimit, setFilesLimit] = usePageSize()
  const [sort, setSort] = useState(DEFAULT_SORT)
  /** Re-sorting reshuffles the whole filtered set, so staying on page 5
   *  would show an arbitrary slice of the new order. Jump back to page 1
   *  so the user sees the top of what they just sorted by. */
  const applySort = (next) => {
    setSort(next)
    setFilesPage(1)
  }

  const [selectedIds, setSelectedIds] = useState(new Set())
  // #16 Files recycle bin — toggle between the active files grid and the Trash.
  const [view, setView] = useState('files') // 'files' | 'trash'
  const [previewFile, setPreviewFile] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  // Live upload progress (0–100) for the Upload dialog. Large files
  // stream over the wire for a while; without visible progress the
  // dialog's "Uploading…" spinner read as "stuck / taking too long".
  const [uploadPct, setUploadPct] = useState(null)
  const [newFolderOpen, setNewFolderOpen] = useState(false)
  // `openMenuId` tracks which row's `…` action menu is open; `menuRect`
  // captures the button's viewport-relative position so the dropdown
  // can render through a portal and won't be clipped by any parent's
  // overflow rules.
  const [openMenuId, setOpenMenuId] = useState(null)
  const [menuRect, setMenuRect] = useState(null)

  function openRowMenu(id, anchorEl) {
    setMenuRect(computeMenuRect(anchorEl, ROW_MENU_HEIGHT))
    setOpenMenuId(id)
  }
  function closeRowMenu() {
    setOpenMenuId(null)
    setMenuRect(null)
  }
  // Owner-only row actions (Rename / Edit tags / Duplicate / Move /
  // Delete) may only be used by the file's owner or a
  // Company Admin — mirrors the backend `assertCanMutateFile` guard and
  // the External Links section's `canManage` gate. Download / Share stay
  // available to any viewer. Without this a non-owner Employee saw every
  // action, clicked one, and got a 403 toast (DF_009 / bug 16872).
  const menuFile = files.find((f) => f.id === openMenuId) ?? null
  const canManageMenuFile = Boolean(
    menuFile && (menuFile.isOwner || actorRole === 'company_admin'),
  )

  // `now` seeds the date-range cutoff. It MUST hold a real timestamp from
  // the first render: a plain `useState(0)` left a window where the cutoff
  // (`now - days`) evaluated against epoch 0, so every file's date compared
  // `>=` a large negative number and the "Last 7/30/90 days" filter silently
  // matched everything. A lazy initializer runs once, off the render path
  // (so `Date.now()` doesn't trip the react-hooks purity rule the way a bare
  // call in render / useMemo would), giving a valid cutoff immediately. The
  // effect refreshes it when the range changes so a long-open page still
  // cuts off relative to "now" rather than page-load time.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    setNow(Date.now())
  }, [dateFilter])

  // Filter pipeline — search ▸ type ▸ date ▸ sort. The smart-folder
  // step was retired alongside the "Recent" chip above; restore it
  // if the Starred / Shared chips ever ship.
  const filteredFiles = useMemo(() => {
    const q = stripLeadingHash(search.trim().toLowerCase())
    const dateMeta = DATE_FILTERS.find((d) => d.value === dateFilter)
    let rows = files
    if (q) {
      rows = rows.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          f.ownerName.toLowerCase().includes(q) ||
          f.tags?.some((t) => t.toLowerCase().includes(q)),
      )
    }
    if (typeFilter !== 'all') {
      rows = rows.filter((f) => f.type === typeFilter)
    }
    if (dateMeta?.days != null) {
      const cutoff = now - dateMeta.days * dayMs
      rows = rows.filter((f) => new Date(f.updatedAt).getTime() >= cutoff)
    }
    rows = [...rows].sort((a, b) => {
      switch (sort) {
        case 'updated-desc':
          return new Date(b.updatedAt) - new Date(a.updatedAt)
        case 'updated-asc':
          return new Date(a.updatedAt) - new Date(b.updatedAt)
        case 'name-asc':
          return a.name.localeCompare(b.name)
        case 'name-desc':
          return b.name.localeCompare(a.name)
        case 'size-desc':
          return b.size - a.size
        case 'size-asc':
          return a.size - b.size
        // `type` keys are the lowercase form of the labels shown in the
        // column (spreadsheet → "Spreadsheet"), so sorting the key gives
        // the same order the user reads on screen.
        case 'type-asc':
          return (a.type ?? '').localeCompare(b.type ?? '')
        case 'type-desc':
          return (b.type ?? '').localeCompare(a.type ?? '')
        case 'owner-asc':
          return (a.ownerName ?? '').localeCompare(b.ownerName ?? '')
        case 'owner-desc':
          return (b.ownerName ?? '').localeCompare(a.ownerName ?? '')
        case 'visibility-asc':
          return (a.visibility ?? '').localeCompare(b.visibility ?? '')
        case 'visibility-desc':
          return (b.visibility ?? '').localeCompare(a.visibility ?? '')
        default:
          return 0
      }
    })
    return rows
  }, [files, search, typeFilter, dateFilter, sort, now])

  // Page window over the filtered set. `currentFilesPage` is clamped so a
  // filter that shrinks the list can't strand the view on a now-empty page;
  // slicing off it keeps the rendered rows and the Pagination bar in agreement.
  const filesTotalPages = Math.max(1, Math.ceil(filteredFiles.length / filesLimit))
  const currentFilesPage = Math.min(filesPage, filesTotalPages)
  const pagedFiles = useMemo(
    () =>
      filteredFiles.slice(
        (currentFilesPage - 1) * filesLimit,
        currentFilesPage * filesLimit,
      ),
    [filteredFiles, currentFilesPage, filesLimit],
  )

  const allFilteredSelected =
    filteredFiles.length > 0 &&
    filteredFiles.every((f) => selectedIds.has(f.id))

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    setSelectedIds(
      allFilteredSelected ? new Set() : new Set(filteredFiles.map((f) => f.id)),
    )
  }

  // Holds the file row queued for the ConfirmDialog. Null means no
  // pending delete. Carries the whole row so the confirm prompt can
  // name the file ("Delete 'website.pdf'?").
  const [fileDeleteTarget, setFileDeleteTarget] = useState(null)
  // Bulk-delete target — keeps the snapshot of selected ids so the
  // user can't change the selection mid-confirm and have the wrong
  // rows deleted.
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState(null)

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/files/${id}`)
      return id
    },
    onSuccess: (id) => {
      toast.success('File moved to Trash.')
      void qc.invalidateQueries({ queryKey: ['files'] })
      void qc.invalidateQueries({ queryKey: ['files', 'trash'] })
      void qc.invalidateQueries({ queryKey: ['admin', 'storage', 'by-user'] })
      void qc.invalidateQueries({ queryKey: ['file-folders'] })
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      setFileDeleteTarget(null)
      closeRowMenu()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function deleteFile(id) {
    // Open the confirm modal instead of immediately deleting — protects
    // against a stray click on the row-action menu's Delete item.
    const file = files.find((f) => f.id === id)
    if (file) setFileDeleteTarget(file)
  }

  // #16 Files recycle bin — Trash listing + Restore (backend: GET /files/trash,
  // POST /files/:id/restore). Only fetched while the Trash view is open.
  const trashQuery = useQuery({
    queryKey: ['files', 'trash'],
    enabled: view === 'trash',
    queryFn: async () => {
      const res = await api.get('/files/trash')
      return res.data?.data ?? []
    },
  })
  const trashedFiles = trashQuery.data ?? []
  const restoreMutation = useMutation({
    mutationFn: async (id) => {
      await api.post(`/files/${id}/restore`)
      return id
    },
    onSuccess: () => {
      toast.success('File restored.')
      void qc.invalidateQueries({ queryKey: ['files'] })
      void qc.invalidateQueries({ queryKey: ['files', 'trash'] })
      void qc.invalidateQueries({ queryKey: ['admin', 'storage', 'by-user'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Bulk-delete is wrapped in a useMutation so the ConfirmDialog can
  // disable its Cancel button while the parallel DELETE fan-out is
  // still in flight (the previous async-function shape gave us no
  // `isPending` flag).
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      // The backend doesn't have a /files/bulk-delete endpoint yet, so
      // we fan out one DELETE per row. allSettled means a single
      // failure doesn't mask the successes — we tally both buckets in
      // the success handler. Phase 2 adds a proper bulk endpoint.
      const results = await Promise.allSettled(
        ids.map((id) => api.delete(`/files/${id}`)),
      )
      return { ids, results }
    },
    onSuccess: ({ ids, results }) => {
      const ok = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.length - ok
      if (ok > 0) {
        toast.success(
          `${ok} ${ok === 1 ? 'file' : 'files'} moved to Trash.`,
        )
      }
      if (failed > 0) {
        const firstErr = results.find((r) => r.status === 'rejected')?.reason
        toast.error(
          `${failed} ${failed === 1 ? 'file' : 'files'} could not be moved to Trash${
            firstErr ? `: ${extractApiError(firstErr).message}` : '.'
          }`,
        )
      }
      void qc.invalidateQueries({ queryKey: ['files'] })
      void qc.invalidateQueries({ queryKey: ['file-folders'] })
      void qc.invalidateQueries({ queryKey: ['admin', 'storage', 'by-user'] })
      // Clear the selection only when at least one row actually went —
      // partial failures keep the failing rows selected so the user
      // can retry on just the survivors.
      if (failed === 0) setSelectedIds(new Set())
      else {
        const succeeded = new Set(
          ids.filter((_id, i) => results[i].status === 'fulfilled'),
        )
        setSelectedIds((prev) => {
          const next = new Set(prev)
          for (const id of succeeded) next.delete(id)
          return next
        })
      }
      setBulkDeleteTarget(null)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function bulkDelete() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    setBulkDeleteTarget({ ids, count: ids.length })
  }

  /** Bulk download: fetch an attachment-disposition signed URL per row
   *  and trigger a real download for each (anchor click, not
   *  `window.open`). Done sequentially with a small stagger so the
   *  browser doesn't drop a burst of downloads. Browsers may show a
   *  one-time "Download multiple files?" prompt — that's expected UX. */
  async function bulkDownload() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    let done = 0
    let failed = 0
    for (const id of ids) {
      try {
        const res = await api.get(`/files/${id}/download`, {
          params: { download: 1 },
        })
        const url = res.data?.url
        if (!url) {
          failed += 1
          continue
        }
        triggerFileDownload(url, res.data?.name)
        done += 1
        await downloadDelay()
      } catch {
        failed += 1
      }
    }
    if (done > 0)
      toast.success(`Downloading ${done} file${done === 1 ? '' : 's'}.`)
    if (failed > 0)
      toast.error(`${failed} file${failed === 1 ? '' : 's'} failed to download.`)
  }

  /** Share the selected file's signed URL (60s TTL). Single-file only:
   *  the toolbar button is disabled for multi-select, and this guard is
   *  the defensive backstop. Copying multiple newline-joined presigned
   *  URLs used to break when pasted into an address bar (the browser
   *  merged them into one malformed request → S3 SignatureDoesNotMatch),
   *  so multi-file share is intentionally not supported — use Download
   *  for several files at once. */
  async function bulkShare() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    if (ids.length > 1) {
      toast.info(
        'Select a single file to copy its share link. Use Download for multiple files.',
      )
      return
    }
    let url
    try {
      // Copy the SHORT app link (POST /files/:id/share), NOT the raw S3
      // download URL. The short link lives on our own domain and returns
      // a friendly "This share link has expired." message once it lapses,
      // whereas a raw presigned S3 URL shows AWS's XML AccessDenied page
      // on expiry. Mirrors the per-row / drawer `shareFile`.
      const res = await api.post(`/files/${ids[0]}/share`)
      url = res.data?.url
    } catch {
      url = null
    }
    if (!url) {
      toast.error('Could not generate a share link.')
      return
    }
    // Shared helper: falls back to a gesture-based execCommand copy when the
    // async Clipboard API is unavailable or refused. See lib/clipboard.js.
    if (!(await copyText(url))) {
      toast.error('Could not write to clipboard.')
      return
    }
    toast.success('Link copied to clipboard (valid for 60 seconds).')
  }


  const uploadMutation = useMutation({
    mutationFn: async ({ file: rawFile, folderId, visibility, tags }) => {
      setUploadPct(0)
      const fd = new FormData()
      fd.append('file', rawFile)
      fd.append('folderId', folderId)
      // Multipart form values are always strings on the wire — the
      // server normalises this back to 'public' / 'private'. Sending
      // a blank string would default to 'public' too, but we send
      // explicit values so any future visibility option doesn't
      // silently land on the default.
      if (visibility) fd.append('visibility', visibility)
      // Optional comma-separated tags from the Upload dialog. The server
      // splits + normalises them (lowercase, dedupe, caps).
      if (tags && tags.trim()) fd.append('tags', tags.trim())
      const res = await api.post('/files', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // The shared axios client has a 15s timeout — fine for JSON
        // calls, but a large file (up to the 100 MB cap) takes far
        // longer to stream than that, and the abort surfaced as the
        // misleading "Can't reach the server" error even on a healthy
        // connection. Give uploads a generous 10-minute ceiling
        // (bounded, so a genuinely stalled connection still fails
        // eventually rather than hanging forever).
        timeout: 10 * 60 * 1000,
        // Real-time progress for the dialog — turns the indefinite
        // "Uploading…" spinner into a visible percentage so a large
        // upload reads as "working", not "stuck".
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100))
        },
      })
      return res.data
    },
    onSuccess: (data) => {
      // A re-upload of the same name in the same location supersedes the
      // existing file as a new version (server-side). Tell the user which
      // happened so a "missing" new row isn't confusing.
      toast.success(
        data?.version > 1
          ? `Uploaded as version ${data.version} of "${data.name}".`
          : 'File added.',
      )
      void qc.invalidateQueries({ queryKey: ['files'] })
      void qc.invalidateQueries({ queryKey: ['admin', 'storage', 'by-user'] })
      // Folder file-counts move when a new file lands — refresh the
      // sidebar cards so the badge stays accurate.
      void qc.invalidateQueries({ queryKey: ['file-folders'] })
    },
    onError: (err) => {
      // Plan-cap hit (storage limit): the global API interceptor already
      // shows the single amber "upgrade your plan" warning, so suppress our
      // own error toast.
      if (isPlanLimitError(err)) return
      toast.error(extractApiError(err).message)
    },
    onSettled: () => setUploadPct(null),
  })

  // Create / delete folder mutations. The card row uses both; the
  // NewFolderModal uses `createFolderMutation`.
  const createFolderMutation = useMutation({
    mutationFn: async ({ name }) => {
      const res = await api.post('/file-folders', { name })
      return res.data
    },
    onSuccess: () => {
      toast.success('Folder created successfully.')
      void qc.invalidateQueries({ queryKey: ['file-folders'] })
    },
    // Errors surface INLINE under the name field via the dialog's
    // try/catch around `mutateAsync`. We deliberately don't toast
    // here — duplicate error feedback (toast + inline) was noisy.
  })

  // Folder edit / delete — driven by the per-card "…" menu and the
  // `EditFolderDialog`. Edit lets the user rename + re-describe; delete
  // is blocked server-side while files still live inside.
  const [editFolderTarget, setEditFolderTarget] = useState(null)
  const [openFolderMenuId, setOpenFolderMenuId] = useState(null)
  const [folderMenuRect, setFolderMenuRect] = useState(null)

  function openFolderMenu(folderId, anchorEl) {
    setFolderMenuRect(computeMenuRect(anchorEl, FOLDER_MENU_HEIGHT))
    setOpenFolderMenuId(folderId)
  }
  function closeFolderMenu() {
    setOpenFolderMenuId(null)
    setFolderMenuRect(null)
  }

  // Dismiss an open row / folder action menu on scroll or resize. The menu
  // is a position:fixed portal placed from the trigger's rect at open time
  // (see computeMenuRect), so once the page scrolls it would otherwise hang
  // in the viewport detached from the button that opened it. Closing it —
  // the same outcome as an outside click — is cleaner than re-tracking the
  // trigger every scroll frame. Capture phase (`true`) also catches scrolls
  // inside any nested scroll container, not just the window.
  useEffect(() => {
    if (!openMenuId && !openFolderMenuId) return undefined
    function dismiss() {
      setOpenMenuId(null)
      setMenuRect(null)
      setOpenFolderMenuId(null)
      setFolderMenuRect(null)
    }
    window.addEventListener('scroll', dismiss, true)
    window.addEventListener('resize', dismiss)
    return () => {
      window.removeEventListener('scroll', dismiss, true)
      window.removeEventListener('resize', dismiss)
    }
  }, [openMenuId, openFolderMenuId])

  const updateFolderMutation = useMutation({
    mutationFn: async ({ id, name }) => {
      const res = await api.patch(`/file-folders/${id}`, { name })
      return res.data
    },
    onSuccess: () => {
      toast.success('Folder updated successfully.')
      void qc.invalidateQueries({ queryKey: ['file-folders'] })
      setEditFolderTarget(null)
    },
    // Inline error in the dialog — see EditFolderDialog's submit catch.
  })

  // Folder pending-delete target. Holds the whole folder so the
  // confirm prompt can render its name.
  const [folderDeleteTarget, setFolderDeleteTarget] = useState(null)

  const deleteFolderMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/file-folders/${id}`)
      return id
    },
    onSuccess: (id) => {
      toast.success('Folder deleted successfully.')
      void qc.invalidateQueries({ queryKey: ['file-folders'] })
      // If we just deleted the folder we were filtering on, clear the
      // filter so the file list isn't permanently stuck on an
      // orphaned id.
      if (selectedFolderId === id) setSelectedFolderId(null)
      setFolderDeleteTarget(null)
      closeFolderMenu()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function deleteFolder(id) {
    const folder = folders.find((f) => f.id === id)
    if (!folder) return
    if (folder.fileCount > 0) {
      // Pre-check guard — folders with files in them are a hard
      // "can't delete" path, so we toast and don't even open the
      // confirm modal.
      toast.error(
        `${folder.name} still contains ${folder.fileCount} ${folder.fileCount === 1 ? 'file' : 'files'}. Move or delete them first.`,
      )
      closeFolderMenu()
      return
    }
    setFolderDeleteTarget(folder)
    closeFolderMenu()
  }

  // ── Row actions — Download, Share, Rename, Tags, Duplicate ────────────
  //
  // Each one fetches/mutates against the real API. Rename + Tags open a
  // small prompt-style dialog; everything else fires immediately.

  // Track which file is being renamed / re-tagged / moved so we can pop
  // the matching dialog. `null` means no dialog is open.
  const [renameTarget, setRenameTarget] = useState(null)
  const [tagsTarget, setTagsTarget] = useState(null)

  async function downloadFile(id) {
    closeRowMenu()
    try {
      const res = await api.get(`/files/${id}/download`, {
        params: { download: 1 },
      })
      const url = res.data?.url
      if (!url) throw new Error('No download URL returned.')
      // Attachment disposition (set server-side via `download=1`) makes
      // the browser SAVE the file rather than render it inline in a new
      // tab. Anchor click instead of `window.open` so it's a clean
      // download with no stray blank tab.
      triggerFileDownload(url, res.data?.name)
    } catch (err) {
      toast.error(extractApiError(err).message)
    }
  }

  async function shareFile(id) {
    closeRowMenu()
    try {
      // Creates a SHORT link on our own domain that redirects to a
      // freshly-signed S3 URL — instead of copying the raw 600-char
      // presigned URL. Lives 60s; each open mints a new short-lived
      // signature server-side.
      const res = await api.post(`/files/${id}/share`)
      const url = res.data?.url
      if (!url) throw new Error('No URL returned.')
      // `copyText` never throws (it returns a boolean), so a clipboard
      // failure can no longer escape into the catch below and be reported
      // through `extractApiError` as if the API had failed. See lib/clipboard.js.
      if (!(await copyText(url))) {
        toast.error('Could not write to clipboard.')
        return
      }
      toast.success('Link copied to clipboard (valid for 60 seconds).')
    } catch (err) {
      toast.error(extractApiError(err).message)
    }
  }

  function openRename(id) {
    const file = files.find((f) => f.id === id)
    if (!file) return
    closeRowMenu()
    setRenameTarget(file)
  }

  function openTags(id) {
    const file = files.find((f) => f.id === id)
    if (!file) return
    closeRowMenu()
    setTagsTarget(file)
  }

  const renameMutation = useMutation({
    mutationFn: async ({ id, name }) => {
      const res = await api.patch(`/files/${id}`, { name })
      return res.data
    },
    onSuccess: () => {
      toast.success('File renamed.')
      void qc.invalidateQueries({ queryKey: ['files'] })
      void qc.invalidateQueries({ queryKey: ['admin', 'storage', 'by-user'] })
      setRenameTarget(null)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const tagsMutation = useMutation({
    mutationFn: async ({ id, tags }) => {
      const res = await api.patch(`/files/${id}`, { tags })
      return res.data
    },
    onSuccess: () => {
      toast.success('Tags updated successfully.')
      void qc.invalidateQueries({ queryKey: ['files'] })
      void qc.invalidateQueries({ queryKey: ['admin', 'storage', 'by-user'] })
      setTagsTarget(null)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Change a file's visibility (public ↔ private) after upload. The
  // backend honours `visibility` on PATCH /files/:id (files.service.update).
  const duplicateMutation = useMutation({
    mutationFn: async (id) => {
      const res = await api.post(`/files/${id}/duplicate`)
      return res.data
    },
    onSuccess: () => {
      toast.success('File duplicated.')
      void qc.invalidateQueries({ queryKey: ['files'] })
      void qc.invalidateQueries({ queryKey: ['admin', 'storage', 'by-user'] })
      void qc.invalidateQueries({ queryKey: ['file-folders'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function duplicateFile(id) {
    closeRowMenu()
    duplicateMutation.mutate(id)
  }

  // ── External links — Google Docs / Sheets / Notion etc. ──────────────
  //
  // Links live alongside files on this page. Backend persists them at
  // `/external-links`; the query is keyed by folder filter so clicking
  // a folder card narrows both files AND links to that folder.
  const linksQuery = useQuery({
    queryKey: ['external-links', { folderId: selectedFolderId }],
    queryFn: async () => {
      const params = selectedFolderId ? { folderId: selectedFolderId } : {}
      const res = await api.get('/external-links', { params })
      return res.data?.data ?? []
    },
  })
  const links = linksQuery.data ?? []

  // Apply the SAME search / type / date filters the Files list uses, so
  // the filter bar governs the WHOLE page consistently. Previously only
  // the date filter reached links, so a search term (e.g. "offer") or a
  // file-type filter narrowed Files while every External Link kept
  // showing (DF_006 / bug 16869). Mirrors the `filteredFiles` predicate;
  // links have no filename or tags, so search matches the equivalents a
  // user can see on the row: title, owner name, provider domain, URL.
  // Links are dated by `updatedAt` (falling back to `createdAt`), the
  // same field the row renders.
  const filteredLinks = useMemo(() => {
    const q = stripLeadingHash(search.trim().toLowerCase())
    const dateMeta = DATE_FILTERS.find((d) => d.value === dateFilter)
    let rows = links
    if (q) {
      rows = rows.filter(
        (l) =>
          (l.title ?? '').toLowerCase().includes(q) ||
          (l.ownerName ?? '').toLowerCase().includes(q) ||
          (l.domain ?? '').toLowerCase().includes(q) ||
          (l.url ?? '').toLowerCase().includes(q),
      )
    }
    // A link has no file "type" (pdf/image/…), so any specific type
    // selection excludes every link — matching a Files list that then
    // shows only that one kind. 'all' is a no-op.
    if (typeFilter !== 'all') {
      rows = []
    }
    if (dateMeta?.days != null) {
      const cutoff = now - dateMeta.days * dayMs
      rows = rows.filter(
        (l) => new Date(l.updatedAt ?? l.createdAt).getTime() >= cutoff,
      )
    }
    return rows
  }, [links, search, typeFilter, dateFilter, now])

  const [addLinkOpen, setAddLinkOpen] = useState(false)
  const [editLinkTarget, setEditLinkTarget] = useState(null)

  const createLinkMutation = useMutation({
    mutationFn: async (dto) => {
      const res = await api.post('/external-links', dto)
      return res.data
    },
    onSuccess: () => {
      toast.success('Link added successfully.')
      void qc.invalidateQueries({ queryKey: ['external-links'] })
      setAddLinkOpen(false)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const updateLinkMutation = useMutation({
    mutationFn: async ({ id, ...dto }) => {
      const res = await api.patch(`/external-links/${id}`, dto)
      return res.data
    },
    onSuccess: () => {
      toast.success('Link updated successfully.')
      void qc.invalidateQueries({ queryKey: ['external-links'] })
      setEditLinkTarget(null)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Pending-delete target for the external-links list. Holds the link
  // row so the confirm prompt can render its title.
  const [linkDeleteTarget, setLinkDeleteTarget] = useState(null)

  const deleteLinkMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/external-links/${id}`)
      return id
    },
    onSuccess: () => {
      toast.success('Link deleted successfully.')
      void qc.invalidateQueries({ queryKey: ['external-links'] })
      setLinkDeleteTarget(null)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function deleteLink(id) {
    const link = links.find((l) => l.id === id)
    if (link) setLinkDeleteTarget(link)
  }

  return (
    <div
      onClick={() => {
        closeRowMenu()
        closeFolderMenu()
      }}
    >
      <Breadcrumb current="Files & Links" />

      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <FolderOpen size={22} className="text-[color:var(--color-accent-strong)]" />
            Files & Links
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            One home for files and links.
          </p>
        </div>
        {/* Files ↔ Trash (recycle bin) view toggle. Section primary actions
         *  ("Upload file" / "Add link") live on their own card headers. */}
        <div className="inline-flex rounded-lg border border-[color:var(--color-rule)] overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setView('files')}
            className={`px-3 py-1.5 cursor-pointer ${view === 'files' ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] font-medium' : 'text-[color:var(--color-ink-muted)]'}`}
          >
            Files
          </button>
          <button
            type="button"
            onClick={() => setView('trash')}
            className={`px-3 py-1.5 cursor-pointer inline-flex items-center gap-1.5 ${view === 'trash' ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] font-medium' : 'text-[color:var(--color-ink-muted)]'}`}
          >
            <Trash2 size={14} /> Trash
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* ── Main column ───────────────────────────────────────────── */}
        {/* min-w-0 lets the 1fr track shrink below the inner table's
         *  min-w-[960px]; without it the table's min-content widened the
         *  whole page and forced a horizontal scrollbar on laptop screens.
         *  The table scrolls inside its own overflow-x-auto wrapper instead. */}
        <div className="min-w-0">
          {view === 'trash' ? (
            <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--color-rule)]">
                <h2 className="text-sm font-semibold text-[color:var(--color-ink-strong)]">Trash</h2>
                <span className="text-xs text-[color:var(--color-ink-muted)]">
                  Deleted files stay here until they’re permanently purged.
                </span>
              </div>
              {trashQuery.isLoading ? (
                <p className="p-8 text-center text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
              ) : trashedFiles.length === 0 ? (
                <p className="p-8 text-center text-sm text-[color:var(--color-ink-muted)]">Trash is empty.</p>
              ) : (
                <ul className="divide-y divide-[color:var(--color-rule)]">
                  {trashedFiles.map((tf) => (
                    <li
                      key={tf.id}
                      className="flex items-center justify-between gap-3 px-4 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm text-[color:var(--color-ink)]">
                          {tf.name}
                        </p>
                        <p className="text-xs text-[color:var(--color-ink-muted)]">
                          {tf.ownerName ?? '—'}
                          {tf.deletedAt ? ` · deleted ${formatDateTime(tf.deletedAt)}` : ''}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => restoreMutation.mutate(tf.id)}
                        disabled={restoreMutation.isPending}
                      >
                        Restore
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : (
          <>
          {/* "No folders yet" top-of-page empty state removed by
           *  request, as was the sidebar Folders card's equivalent copy.
           *  The card's '+' button carries the create affordance. */}

          {/* Toolbar — shared FilterRow primitive. PillSelect components
           *  (custom popovers with icon affordances) are kept as
           *  siblings since they don't fit the FilterRow.Select shape. */}
          <FilterRow>
            <FilterRow.Search
              value={search}
              onChange={setSearch}
              placeholder="Search Files, Owners, Tags"
            />
            <PillSelect
              icon={FilterIcon}
              label="Filter by file type"
              value={typeFilter}
              onChange={setTypeFilter}
              options={TYPE_FILTERS}
            />
            <PillSelect
              icon={Calendar}
              label="Filter by date added"
              value={dateFilter}
              onChange={setDateFilter}
              options={DATE_FILTERS}
            />
            {/* Clear-all chip — appears only when a filter is active
             *  (search text, a non-"All files" type, or a non-"Any time"
             *  date). Resets all three at once so users don't have to
             *  undo each control individually. `boxed` gives it the same
             *  bordered-pill look as the type / date controls so it reads
             *  as a sibling filter box rather than a stray text link. */}
            {/* "Clear all" appears for the TYPE/DATE filters only — the search
             *  field carries its own X, so showing both for search was
             *  redundant. When shown, it still clears the search too. */}
            <FilterRow.Clear
              boxed
              show={typeFilter !== 'all' || dateFilter !== 'all'}
              onClick={() => {
                setSearch('')
                setTypeFilter('all')
                setDateFilter('all')
              }}
            />
          </FilterRow>

          {/* Bulk action toolbar */}
          {selectedIds.size > 0 ? (
            <section className="rounded-xl border border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] p-3 mb-4 flex flex-wrap items-center gap-3">
              <p className="text-sm font-medium text-[color:var(--color-accent-strong)]">
                {selectedIds.size} selected
              </p>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                {/* Clear the whole selection in one click — previously the
                 *  only way to deselect was unchecking each row. */}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setSelectedIds(new Set())}
                  title="Clear the current selection"
                >
                  <X size={14} /> Deselect All
                </Button>
                <Button size="sm" variant="secondary" onClick={bulkDownload}>
                  <Download size={14} /> Download
                </Button>
                {/* Share is a SINGLE-file action. A presigned link is
                 *  meant to be opened directly; copying several
                 *  newline-joined links led users to paste the whole
                 *  block into one address bar, where the browser merged
                 *  them into one malformed request and S3 returned
                 *  SignatureDoesNotMatch. Disable for multi-select and
                 *  point users at Download (which opens each file) for
                 *  bulk. */}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={bulkShare}
                  disabled={selectedIds.size !== 1}
                  title={
                    selectedIds.size !== 1
                      ? 'Select a single file to copy its share link. Use Download for multiple files.'
                      : 'Copy a 60-second share link'
                  }
                >
                  <Copy size={14} /> Copy Link
                </Button>
                <Button size="sm" variant="danger" onClick={bulkDelete}>
                  <Trash2 size={14} /> Delete
                </Button>
              </div>
            </section>
          ) : null}

          {/* Files table — note: `overflow-visible` (not hidden) so
           *  the row-action `MoreHorizontal` dropdown can extend
           *  beyond the table edge. With `overflow-hidden` the menu
           *  gets clipped when the table only has a row or two. */}
          <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm overflow-visible">
            <div className="px-6 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between">
              <div>
                <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
                  Files
                </h2>
                <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5">
                  {filteredFiles.length}{' '}
                  {filteredFiles.length === 1 ? 'file' : 'files'}
                </p>
              </div>
              {/* Section primary action — the page-header cluster was
               *  removed 2026-05-22 with the intent that each section
               *  card carries its own action, but this button was lost
               *  in that refactor: the Upload modal existed with no
               *  trigger anywhere on the page (the Dashboard "Upload
               *  File" quick action landed users here with no way to
               *  upload). Folder-required guard before opening: files
               *  cannot exist outside a folder, so with none yet we
               *  prompt folder creation first. */}
              {/* Read-only page: files/links/folders are added INSIDE a
               *  Space (which auto-creates the folder + stamps the
               *  spaceId); they roll up here scoped to the Spaces you can
               *  see. The Upload / Add link / New folder actions were
               *  removed from this page by request. */}
            </div>
            {filesQuery.isLoading ? (
              <div className="p-12 text-center text-sm text-[color:var(--color-ink-muted)]">
                Loading files…
              </div>
            ) : filesQuery.isError ? (
              <div className="p-12 text-center">
                <p className="text-sm text-[color:var(--color-priority-urgent)] font-medium">
                  {extractApiError(filesQuery.error).message}
                </p>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="p-12 text-center">
                <FileIcon
                  size={28}
                  className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]"
                />
                <p className="text-sm text-[color:var(--color-ink-strong)] font-medium">
                  {files.length === 0 ? 'No files yet' : 'No files match'}
                </p>
                <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                  {files.length === 0
                    ? 'Files attached to your spaces appear here.'
                    : 'Try widening the type / date filter or clearing search.'}
                </p>
              </div>
            ) : (
              // `overflow-x-auto` would clip the row-action dropdown
              // vertically too — most browsers couple overflow-x and
              // overflow-y. Switch off explicitly for the table wrap.
              <div className="overflow-x-auto overflow-y-visible">
                {/* `table-fixed` + explicit column widths so the File Name
                 *  column keeps a readable share (and truncates long names,
                 *  with the full value on hover) instead of collapsing to a
                 *  few characters — an auto-layout table handed the leftover
                 *  width to the other columns and ignored the cell min-width.
                 *  `min-w` makes the whole table scroll on narrow containers
                 *  rather than squeezing any one column. */}
                <table className="w-full table-fixed text-left text-sm min-w-[860px]">
                  <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
                    <tr className="text-left border-b-2 border-[color:var(--color-rule)]">
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
                        />
                      </th>
                      {/* File Name is the FLEXIBLE column: `w-full` makes it
                       *  absorb all leftover width so the fixed columns to
                       *  its right (Type…Updated) are never pushed out of
                       *  the container. Its <td> pairs this with `max-w-0`
                       *  so a long filename truncates rather than widening
                       *  the table — `truncate` alone can't do that, since
                       *  an auto-layout table still sizes the column to the
                       *  text's full intrinsic width. */}
                      {/* Flexible column: `w-full` absorbs the leftover table
                       *  width (table-fixed honours this reliably), so the name
                       *  gets the widest readable share. */}
                      <SortableTh
                        label="File Name"
                        sortKey="name"
                        sort={sort}
                        onSort={applySort}
                        className="w-full"
                      />
                      <SortableTh
                        label="Type"
                        sortKey="type"
                        sort={sort}
                        onSort={applySort}
                        className="whitespace-nowrap w-28"
                      />
                      {/* Size and Updated default to descending — biggest
                       *  file / most recent first is the useful first click. */}
                      <SortableTh
                        label="Size"
                        sortKey="size"
                        sort={sort}
                        onSort={applySort}
                        defaultDir="desc"
                        className="whitespace-nowrap w-24"
                      />
                      <SortableTh
                        label="Owner"
                        sortKey="owner"
                        sort={sort}
                        onSort={applySort}
                        className="whitespace-nowrap w-40"
                      />
                      <SortableTh
                        label="Visibility"
                        sortKey="visibility"
                        sort={sort}
                        onSort={applySort}
                        className="whitespace-nowrap w-28"
                      />
                      <SortableTh
                        label="Updated"
                        sortKey="updated"
                        sort={sort}
                        onSort={applySort}
                        defaultDir="desc"
                        className="whitespace-nowrap w-44"
                      />
                      <th className="px-4 py-3 w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedFiles.map((f) => {
                      const meta = TYPE_META[f.type] ?? TYPE_META.other
                      const Icon = meta.icon
                      const isSelected = selectedIds.has(f.id)
                      const isMenuOpen = openMenuId === f.id
                      return (
                        <tr
                          key={f.id}
                          className={
                            'border-b border-[color:var(--color-rule)] last:border-b-0 hover:bg-[color:var(--color-canvas)] ' +
                            (isSelected ? 'bg-[color:var(--color-accent-bg)]' : '')
                          }
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(f.id)}
                              className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
                            />
                          </td>
                          <td
                            className="px-4 py-3 cursor-pointer"
                            onClick={() => setPreviewFile(f)}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span
                                className={
                                  'h-8 w-8 rounded-md border flex items-center justify-center shrink-0 ' +
                                  TONE_BG[meta.tone]
                                }
                              >
                                <Icon size={14} />
                              </span>
                              <div className="min-w-0">
                                <p
                                  className="font-medium text-[color:var(--color-ink-strong)] truncate flex items-center gap-1.5"
                                  title={f.name}
                                >
                                  <span className="truncate">{f.name}</span>
                                </p>
                                {f.tasks?.length > 0 ? (
                                  <p
                                    className="mt-0.5 flex items-center gap-1 text-xs text-[color:var(--color-ink-muted)] truncate"
                                    title={`Attached to task: ${f.tasks
                                      .map((t) => t.name)
                                      .join(', ')}`}
                                  >
                                    <CornerDownRight
                                      size={11}
                                      className="shrink-0"
                                      aria-hidden="true"
                                    />
                                    <span className="truncate">
                                      {f.tasks.map((t) => t.name).join(', ')}
                                    </span>
                                  </p>
                                ) : null}
                                {f.tags?.length > 0 ? (
                                  <div className="mt-0.5 flex flex-wrap gap-1">
                                    {f.tags.map((t) => (
                                      <span
                                        key={t}
                                        className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]"
                                      >
                                        #{t}
                                      </span>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[color:var(--color-ink-muted)] whitespace-nowrap">
                            {meta.label}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-[color:var(--color-ink)] whitespace-nowrap">
                            {fmtSize(f.size)}
                          </td>
                          {/* Owner uses the available width, capped so a very
                           *  long name ellipsises instead of stretching the
                           *  table; `min-w-0` lets the inner flex child shrink
                           *  so `truncate` applies. The name also carries a
                           *  `title` so the full value shows on hover whenever
                           *  it is truncated. */}
                          <td className="px-4 py-3 max-w-[240px]">
                            <div className="inline-flex items-center gap-2 min-w-0 max-w-full">
                              {/* Shared <Avatar /> — renders the
                               *  owner's uploaded picture when
                               *  available, otherwise initials on
                               *  the brand ocean→cyan gradient. */}
                              <Avatar
                                name={f.ownerName}
                                avatarUrl={f.ownerAvatarUrl}
                                size="sm"
                              />
                              <span
                                title={f.ownerName}
                                className="text-[color:var(--color-ink)] truncate"
                              >
                                {f.ownerName}
                                {f.isOwner ? (
                                  <span className="ml-1 text-xs text-[color:var(--color-ink-muted)]">
                                    (you)
                                  </span>
                                ) : null}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {f.visibility === 'private' ? (
                              <span
                                title="Private — only you and your Company Admin or Sub Admin can see this file"
                                className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]"
                              >
                                Private
                              </span>
                            ) : (
                              <span
                                title="Public — anyone who can access its Space (your department, plus company-wide members and admins) can see this file"
                                className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]"
                              >
                                Public
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-[color:var(--color-ink-muted)] whitespace-nowrap tabular-nums">
                            {formatDateTime(f.updatedAt)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (isMenuOpen) closeRowMenu()
                                else openRowMenu(f.id, e.currentTarget)
                              }}
                              aria-label="More actions"
                              className="p-1 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                            >
                              <MoreVertical size={16} />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {filteredFiles.length > 0 ? (
              <Pagination
                page={currentFilesPage}
                totalPages={filesTotalPages}
                total={filteredFiles.length}
                limit={filesLimit}
                onPageChange={setFilesPage}
                onLimitChange={(n) => {
                  setFilesLimit(n)
                  setFilesPage(1)
                }}
              />
            ) : null}
          </section>

          {/* External Links — sits below the files table so the page
           *  reads top-to-bottom: folders → files → links. Filtered by
           *  the same selectedFolderId as the files list so clicking a
           *  folder card narrows both. */}
          <LinksSection
            links={filteredLinks}
            totalCount={links.length}
            dateFiltered={dateFilter !== 'all'}
            loading={linksQuery.isLoading}
            error={linksQuery.isError ? linksQuery.error : null}
            selectedFolderId={selectedFolderId}
            currentUserId={currentUserId}
            canManageOthers={actorRole === 'company_admin'}
            onEdit={(link) => setEditLinkTarget(link)}
            onDelete={deleteLink}
          />
          </>
          )}
        </div>

        {/* ── Right sidebar — order: Storage → Folders → Storage by
         *   member. Folders sits between the two storage cards so the
         *   admin's "where did this byte go?" scan reads as a column:
         *   total quota → which folder is filling up → who's
         *   responsible. */}
        <aside className="space-y-4">
          <StorageCard
            storage={storageQuery.data}
            loading={storageQuery.isLoading}
            canUpgrade={canUpgradeStorage}
            onUpgrade={() => navigate('/billing')}
          />
          <FoldersCard
            folders={folders}
            loading={foldersQuery.isLoading}
            selectedFolderId={selectedFolderId}
            openFolderMenuId={openFolderMenuId}
            onSelect={(id) =>
              setSelectedFolderId(selectedFolderId === id ? null : id)
            }
            onClear={() => setSelectedFolderId(null)}
            onOpenFolderMenu={openFolderMenu}
            onCloseFolderMenu={closeFolderMenu}
            canManage={!isExternalMember}
          />
          {isAdminViewer ? (
            <StorageByMemberCard
              data={storageByUserQuery.data}
              loading={storageByUserQuery.isLoading}
            />
          ) : null}
        </aside>
      </div>

      {/* Modals */}
      <UploadFileModal
        open={uploadOpen}
        folders={folders}
        defaultFolderId={selectedFolderId}
        uploading={uploadMutation.isPending}
        progress={uploadPct}
        onClose={() => setUploadOpen(false)}
        onUploaded={async ({ file, folderId, visibility, tags }) => {
          await uploadMutation.mutateAsync({ file, folderId, visibility, tags })
          setUploadOpen(false)
        }}
      />
      <NewFolderModal
        open={newFolderOpen}
        saving={createFolderMutation.isPending}
        onClose={() => setNewFolderOpen(false)}
        onCreate={async ({ name }) => {
          await createFolderMutation.mutateAsync({ name })
          setNewFolderOpen(false)
        }}
      />
      <EditFolderDialog
        folder={editFolderTarget}
        saving={updateFolderMutation.isPending}
        onClose={() => setEditFolderTarget(null)}
        onSave={async ({ name }) => {
          // mutateAsync so the dialog's `submit` try/catch can surface
          // the server error inline. `.mutate` swallows the rejection
          // and the dialog would never know to show the error.
          await updateFolderMutation.mutateAsync({
            id: editFolderTarget.id,
            name,
          })
        }}
      />
      <RenameFileDialog
        file={renameTarget}
        saving={renameMutation.isPending}
        onClose={() => setRenameTarget(null)}
        onSave={(name) =>
          renameMutation.mutate({ id: renameTarget.id, name })
        }
      />
      <EditTagsDialog
        file={tagsTarget}
        saving={tagsMutation.isPending}
        onClose={() => setTagsTarget(null)}
        onSave={(tags) =>
          tagsMutation.mutate({ id: tagsTarget.id, tags })
        }
      />
      <FilePreviewDrawer
        file={previewFile}
        onClose={() => setPreviewFile(null)}
        onDownload={downloadFile}
        onShare={shareFile}
      />
      <AddLinkDialog
        open={addLinkOpen}
        saving={createLinkMutation.isPending}
        folders={folders}
        defaultFolderId={selectedFolderId}
        onClose={() => setAddLinkOpen(false)}
        onSave={(dto) => createLinkMutation.mutate(dto)}
      />
      <EditLinkDialog
        link={editLinkTarget}
        saving={updateLinkMutation.isPending}
        folders={folders}
        onClose={() => setEditLinkTarget(null)}
        onSave={(dto) =>
          updateLinkMutation.mutate({ id: editLinkTarget.id, ...dto })
        }
      />

      {/* Confirm-before-delete modals for every destructive flow on the
       *  page — files (single + bulk), folders, links. Each uses the
       *  shared ConfirmDialog so the look matches every other
       *  destructive prompt in the app and stays accessible (Escape to
       *  cancel, focus trap, etc.). */}
      <ConfirmDialog
        open={Boolean(fileDeleteTarget)}
        title={
          fileDeleteTarget
            ? `Delete "${fileDeleteTarget.name}"?`
            : 'Delete this file?'
        }
        message="This file will be moved to Trash. You can restore it from Trash until it is permanently purged."
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() =>
          deleteMutation.isPending ? undefined : setFileDeleteTarget(null)
        }
        onConfirm={() => {
          if (fileDeleteTarget?.id) deleteMutation.mutate(fileDeleteTarget.id)
        }}
      />
      <ConfirmDialog
        open={Boolean(bulkDeleteTarget)}
        title={
          bulkDeleteTarget
            ? `Delete ${bulkDeleteTarget.count} ${bulkDeleteTarget.count === 1 ? 'file' : 'files'}?`
            : 'Delete selected files?'
        }
        message="These files will be moved to Trash. You can restore them from Trash until they are permanently purged."
        confirmLabel={
          bulkDeleteTarget
            ? `Delete ${bulkDeleteTarget.count} ${bulkDeleteTarget.count === 1 ? 'file' : 'files'}`
            : 'Delete'
        }
        variant="danger"
        confirming={bulkDeleteMutation.isPending}
        onCancel={() =>
          bulkDeleteMutation.isPending ? undefined : setBulkDeleteTarget(null)
        }
        onConfirm={() => {
          if (bulkDeleteTarget?.ids?.length) {
            bulkDeleteMutation.mutate(bulkDeleteTarget.ids)
          }
        }}
      />
      <ConfirmDialog
        open={Boolean(folderDeleteTarget)}
        title={
          folderDeleteTarget
            ? `Delete folder "${folderDeleteTarget.name}"?`
            : 'Delete this folder?'
        }
        message="This folder will be permanently removed. This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteFolderMutation.isPending}
        onCancel={() =>
          deleteFolderMutation.isPending
            ? undefined
            : setFolderDeleteTarget(null)
        }
        onConfirm={() => {
          if (folderDeleteTarget?.id) {
            deleteFolderMutation.mutate(folderDeleteTarget.id)
          }
        }}
      />
      <ConfirmDialog
        open={Boolean(linkDeleteTarget)}
        title={
          linkDeleteTarget
            ? `Delete "${linkDeleteTarget.title}"?`
            : 'Delete this link?'
        }
        message="This external link will be permanently removed. This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteLinkMutation.isPending}
        onCancel={() =>
          deleteLinkMutation.isPending ? undefined : setLinkDeleteTarget(null)
        }
        onConfirm={() => {
          if (linkDeleteTarget?.id) {
            deleteLinkMutation.mutate(linkDeleteTarget.id)
          }
        }}
      />

      {/* Row-action menu — portalled to <body> so it escapes the
       *  table / page-scroll overflow constraints and is always
       *  visible no matter how short the table is. */}
      {openMenuId && menuRect
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                // `computeMenuRect` returns either `top` (open
                // downward) or `bottom` (flipped upward when there
                // isn't room below the anchor).
                ...(menuRect.top != null
                  ? { top: menuRect.top }
                  : { bottom: menuRect.bottom }),
                right: menuRect.right,
                zIndex: 60,
              }}
              className="w-44 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-lg py-1"
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem
                icon={Download}
                label="Download"
                onClick={() => downloadFile(openMenuId)}
              />
              <MenuItem
                icon={Copy}
                label="Copy link"
                onClick={() => shareFile(openMenuId)}
              />
              {canManageMenuFile ? (
                <>
                  <MenuItem
                    icon={Pencil}
                    label="Rename"
                    onClick={() => openRename(openMenuId)}
                  />
                  <MenuItem
                    icon={Tag}
                    label={
                      (files.find((f) => f.id === openMenuId)?.tags?.length ?? 0) > 0
                        ? 'Edit tags'
                        : 'Add tags'
                    }
                    onClick={() => openTags(openMenuId)}
                  />
                  <MenuItem
                    icon={Copy}
                    label="Duplicate"
                    onClick={() => duplicateFile(openMenuId)}
                  />
                  <div className="border-t border-[color:var(--color-rule)] my-1" />
                  <MenuItem
                    icon={Trash2}
                    label="Delete"
                    danger
                    onClick={() => {
                      deleteFile(openMenuId)
                      closeRowMenu()
                    }}
                  />
                </>
              ) : null}
            </div>,
            document.body,
          )
        : null}

      {/* Folder-action menu — same portal pattern as the row menu so
       *  it isn't clipped by the surrounding card / grid overflow. */}
      {openFolderMenuId && folderMenuRect
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                ...(folderMenuRect.top != null
                  ? { top: folderMenuRect.top }
                  : { bottom: folderMenuRect.bottom }),
                right: folderMenuRect.right,
                zIndex: 60,
              }}
              className="w-44 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-lg py-1"
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem
                icon={Pencil}
                label="Edit folder"
                onClick={() => {
                  const folder = folders.find(
                    (f) => f.id === openFolderMenuId,
                  )
                  if (folder) setEditFolderTarget(folder)
                  closeFolderMenu()
                }}
              />
              <div className="border-t border-[color:var(--color-rule)] my-1" />
              <MenuItem
                icon={Trash2}
                label="Delete"
                danger
                onClick={() => deleteFolder(openFolderMenuId)}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────
function PillSelect({ icon: Icon, label, value, onChange, options }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // Icon-only control with no visible <label>: without an explicit name
        // a screen reader announces only the selected option, so "All files"
        // and "Any time" are indistinguishable from each other.
        aria-label={label}
        // Transparent + bordered to match the shared FilterRow.Search
        // / FilterRow.Select primitives. The previous `bg-canvas`
        // (soft grey) painted these dropdowns as standalone chips
        // that didn't blend with the FilterRow card shell around
        // them — user flagged this on the Files & Links page after
        // we'd already fixed the same pattern on the platform
        // surfaces.
        className="appearance-none h-9 pl-9 pr-9 rounded-md border border-[color:var(--color-rule)] bg-transparent text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer transition-colors"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {/* Leading icon — explicitly marked so the global "rotate the
       *  chevron while the popup is open" rule can never flip it (see
       *  the `data-dropdown-leading` rule in index.css). Only the
       *  trailing chevron below should rotate. */}
      <Icon
        size={14}
        data-dropdown-leading="true"
        className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)] pointer-events-none"
      />
      <ChevronDown
        size={14}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)] pointer-events-none"
      />
    </div>
  )
}

function MenuItem({ icon: Icon, label, danger, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'w-full text-left px-3 py-1.5 text-sm inline-flex items-center gap-2 cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
        (danger
          ? 'text-[color:var(--color-priority-urgent)]'
          : 'text-[color:var(--color-ink)]')
      }
    >
      <Icon size={13} /> {label}
    </button>
  )
}

function StorageCard({ storage, loading, canUpgrade = false, onUpgrade }) {
  const used = storage?.usedBytes ?? 0
  // `totalBytes === null` is the Enterprise / unlimited signal from
  // the API. Treat it as "no cap" — hide the bar and the upgrade CTA,
  // and render the headline as "X used · Unlimited".
  const total = storage?.totalBytes
  const isUnlimited = total === null || total === undefined
  const pct = !isUnlimited && total > 0 ? Math.round((used / total) * 100) : 0
  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm p-5">
      <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mb-3">
        Storage
      </h2>
      {loading && !storage ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          Loading…
        </p>
      ) : (
        <>
          <p className="font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums">
            {fmtSize(used)}
            <span className="text-base text-[color:var(--color-ink-muted)] font-normal">
              {' '}
              / {isUnlimited ? 'Unlimited' : fmtSize(total)}
            </span>
          </p>
          {!isUnlimited ? (
            <div className="mt-2 h-2 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
              <div
                className="h-full rounded-full bg-[color:var(--color-accent)]"
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          ) : null}
          <ul className="mt-4 space-y-2">
            {STORAGE_BUCKETS.map((b) => {
              const Icon = b.icon
              const sizeBytes = storage?.breakdown?.[b.key] ?? 0
              return (
                <li key={b.key} className="flex items-center gap-2 text-sm">
                  <span
                    className={
                      'h-7 w-7 rounded-md border flex items-center justify-center shrink-0 ' +
                      TONE_BG[b.tone]
                    }
                  >
                    <Icon size={12} />
                  </span>
                  <span className="text-[color:var(--color-ink)] flex-1">
                    {b.kind}
                  </span>
                  <span className="text-[color:var(--color-ink-strong)] font-semibold tabular-nums">
                    {fmtSize(sizeBytes)}
                  </span>
                </li>
              )
            })}
          </ul>
          {!isUnlimited ? (
            canUpgrade ? (
              <Button className="w-full mt-4" onClick={onUpgrade}>
                Upgrade Storage
              </Button>
            ) : (
              <p className="mt-4 text-xs text-[color:var(--color-ink-muted)] text-center">
                Storage is part of your company plan. Contact your Company
                Admin to upgrade.
              </p>
            )
          ) : null}
        </>
      )}
    </section>
  )
}

/** Per-member storage breakdown — admin-only sidebar card so the
 *  Company Admin can see who's burning the most of the shared plan
 *  storage pool. Sorts heaviest-first; each row shows the user's
 *  initials, name, file count, bytes, and a relative bar against
 *  the top consumer so the comparison reads at a glance. */
function StorageByMemberCard({ data, loading }) {
  const byUser = data?.byUser ?? []
  const totalBytes = data?.totalBytes
  const isUnlimited = totalBytes === null || totalBytes === undefined
  // Bar scale — relative to the top consumer when on an unlimited
  // plan (so we can still rank heaviest vs lightest), or relative to
  // the plan cap when capped (so each bar shows "how much of the
  // pool this user is responsible for").
  const topBytes = byUser[0]?.bytes ?? 0
  const scaleBy = isUnlimited ? topBytes : totalBytes
  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm p-5">
      <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mb-1">
        Storage by Member
      </h2>
      <p className="text-xs text-[color:var(--color-ink-muted)] mb-3">
        Everyone shares the same plan storage. Heaviest users first.
      </p>
      {loading && !data ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
      ) : byUser.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] italic">
          No uploads yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {byUser.map((u) => {
            const pct =
              scaleBy > 0 ? Math.min(100, (u.bytes / scaleBy) * 100) : 0
            return (
              <li key={u.userId ?? u.fullName} className="space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  {/* Shared <Avatar /> — uses the user's uploaded
                   *  picture when available, falls back to initials
                   *  on the brand ocean→cyan gradient. */}
                  <Avatar
                    name={u.fullName}
                    avatarUrl={u.avatarUrl}
                    size="xs"
                  />
                  <span
                    className="flex-1 min-w-0 truncate text-[color:var(--color-ink)]"
                    title={u.emailId ?? u.fullName}
                  >
                    {u.fullName}
                  </span>
                  <span className="text-xs tabular-nums text-[color:var(--color-ink-strong)] font-semibold">
                    {fmtSize(u.bytes)}
                  </span>
                </div>
                <div className="ml-8 h-1.5 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[color:var(--color-accent)]"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="ml-8 text-[12px] text-[color:var(--color-ink-muted)] tabular-nums">
                  {u.files} {u.files === 1 ? 'file' : 'files'}
                  {!isUnlimited
                    ? ` · ${Math.round((u.bytes / totalBytes) * 100)}% of plan`
                    : ''}
                </p>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

// The previous `avatarColorFor` + `initialsFor` helpers were retired
// on 2026-05-19 when the page migrated to the shared <Avatar />
// component (see `src/components/Avatar.jsx`). The new component
// reads the owner's uploaded `avatarUrl` and falls back to initials
// on the brand ocean→cyan gradient — same look the UserMenu has used
// since the blue-palette refresh.


/**
 * Sidebar Folders card — vertical list view used in place of the
 * old top-of-page card grid. Click a row to filter both files and
 * external links to that folder; click again to clear. Each row
 * gets a hover-only "…" menu for Edit / Delete (driven by the
 * existing `openFolderMenu` portal in FilesPage).
 */
function FoldersCard({
  folders,
  loading,
  selectedFolderId,
  openFolderMenuId,
  onSelect,
  onClear,
  onOpenFolderMenu,
  onCloseFolderMenu,
  // [External Collaboration] false for externals — folders are tenant-wide
  // (company-wide), so the rename/delete "…" menu is hidden. They still see the
  // grant-scoped folder chips to filter their files by Space.
  canManage = true,
}) {
  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
            Folders
          </h2>
          <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5">
            Click to filter, click again to clear. Folders tagged{' '}
            <span className="font-semibold text-[color:var(--color-accent-strong)]">
              Space
            </span>{' '}
            are created automatically for files attached to that Space.
          </p>
        </div>
        {/* New-folder action removed — folders are created automatically
         *  when a file/link is attached to a Space (read-only page). */}
      </div>

      {selectedFolderId ? (
        <button
          type="button"
          onClick={onClear}
          className="w-full mb-2 inline-flex items-center justify-center gap-1.5 h-7 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-xs font-medium text-[color:var(--color-ink)] hover:border-[color:var(--color-ink-muted)] cursor-pointer"
        >
          <X size={12} />
          Clear Filter
        </button>
      ) : null}

      {loading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          Loading folders…
        </p>
      ) : folders.length === 0 ? (
        /* Empty-state copy removed by request — the card's own '+' button
         *  is the affordance, so an empty card reads as "no folders" on its
         *  own. Kept as an explicit null branch so the list below never
         *  renders an empty <ul>. */
        null
      ) : (
        <ul className="space-y-1">
          {folders.map((f) => {
            const active = selectedFolderId === f.id
            // Space-backed auto-folders get a distinct icon (layers) and
            // colour family so they read as "this mirrors a Space" rather
            // than "a folder I made". Plain folders keep the amber folder
            // glyph. This is the core of the tester's clarity fix.
            const isSpace = f.kind === 'space'
            const FolderIcon = isSpace ? Layers : Folder
            return (
              <li key={f.id} className="group relative">
                <button
                  type="button"
                  onClick={() => onSelect(f.id)}
                  className={
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left cursor-pointer transition-colors ' +
                    (active
                      ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]'
                      : 'text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
                  }
                >
                  <span
                    className={
                      'inline-flex h-7 w-7 items-center justify-center rounded-md border shrink-0 ' +
                      (active
                        ? 'bg-[color:var(--color-accent)] text-white border-[color:var(--color-accent)]'
                        : isSpace
                          ? 'bg-[#EDE9FE] text-[#7C3AED] border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10'
                          : 'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border-[color:var(--color-warning-border)]')
                    }
                  >
                    <FolderIcon size={14} />
                  </span>
                  <span
                    className="text-sm font-medium truncate flex-1 pr-6 inline-flex items-center gap-1.5"
                    title={
                      isSpace
                        ? `${f.name} — auto-created for files attached to this Space`
                        : f.name
                    }
                  >
                    <span className="truncate">{f.name}</span>
                    {isSpace ? (
                      <span
                        title="Created automatically for files attached to tasks in this Space"
                        className="shrink-0 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10"
                      >
                        <Layers size={9} />
                        Space
                      </span>
                    ) : null}
                  </span>
                  {/* The count fades out when the row is hovered or
                   *  the folder menu is open on this row — that hands
                   *  the right-edge spot over to the "…" button which
                   *  fades in. Avoids the two visuals stacking. */}
                  <span
                    className={
                      'text-xs text-[color:var(--color-ink-muted)] tabular-nums shrink-0 transition-opacity ' +
                      (openFolderMenuId === f.id
                        ? 'opacity-0'
                        : 'group-hover:opacity-0 group-focus-within:opacity-0')
                    }
                  >
                    {f.itemCount ?? f.fileCount}
                  </span>
                </button>
                {canManage ? (
                  <button
                    type="button"
                    aria-label="Folder actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (openFolderMenuId === f.id) {
                        onCloseFolderMenu()
                      } else {
                        onOpenFolderMenu(f.id, e.currentTarget)
                      }
                    }}
                    className={
                      'absolute top-1/2 -translate-y-1/2 right-1 inline-flex h-6 w-6 items-center justify-center rounded text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card)] hover:text-[color:var(--color-ink-strong)] cursor-pointer transition-opacity ' +
                      (openFolderMenuId === f.id
                        ? 'opacity-100'
                        : 'opacity-0 group-hover:opacity-100 focus:opacity-100')
                    }
                  >
                    <MoreVertical size={12} />
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function FilePreviewDrawer({ file, onClose, onDownload, onShare }) {
  // Download a specific version from the history list. Fetches a fresh
  // 60-second presigned URL for that version (the server resolves its S3
  // key) and opens it; `download: 1` forces a save with the real name.
  async function downloadVersion(version) {
    try {
      const res = await api.get(`/files/${file.id}/download`, {
        params: { download: 1, version },
      })
      const url = res.data?.url
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error(extractApiError(err).message)
    }
  }

  // Image preview state — only fetched when the selected file is an
  // image type. `/files/:id/download` returns a 60-second presigned URL
  // which the browser uses to render the actual bitmap inline. Without
  // this fetch the panel only showed a placeholder Lucide icon, which
  // QA flagged: clicking an image opened the panel but the preview
  // tile rendered as a generic icon rather than the real image.
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(false)

  useEffect(() => {
    // Reset whenever a different file is selected so the previous
    // image doesn't flash while the new one is being signed.
    setPreviewUrl(null)
    setPreviewError(false)
    if (!file || file.type !== 'image') {
      setPreviewLoading(false)
      return
    }
    let cancelled = false
    setPreviewLoading(true)
    api
      .get(`/files/${file.id}/download`)
      .then((res) => {
        if (cancelled) return
        const url = res.data?.url
        if (url) setPreviewUrl(url)
        else setPreviewError(true)
      })
      .catch(() => {
        if (cancelled) return
        setPreviewError(true)
      })
      .finally(() => {
        if (cancelled) return
        setPreviewLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [file])

  if (!file) return null
  const meta = TYPE_META[file.type] ?? TYPE_META.other
  const Icon = meta.icon
  const isImage = file.type === 'image'
  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-stretch justify-end"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="w-[420px] max-w-full h-full bg-[color:var(--color-card)] border-l border-[color:var(--color-rule)] shadow-2xl overflow-y-auto">
        <div className="px-5 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={
                'h-8 w-8 rounded-md border flex items-center justify-center shrink-0 ' +
                TONE_BG[meta.tone]
              }
            >
              <Icon size={14} />
            </span>
            <p className="font-semibold text-[color:var(--color-ink-strong)] truncate">
              {file.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Preview tile — renders the real image bitmap for image
           *  files; falls back to the type icon for everything else
           *  (PDF / spreadsheet / docs etc. — proper inline preview
           *  for those would need a PDF viewer / Office viewer, which
           *  isn't wired up yet). On image fetch error we ALSO fall
           *  back to the icon so the panel never reads as broken. */}
          <div className="rounded-md border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-canvas)] h-44 flex items-center justify-center overflow-hidden">
            {isImage && previewLoading ? (
              <div className="h-14 w-14 rounded-md bg-[color:var(--color-rule)] animate-pulse" />
            ) : isImage && previewUrl && !previewError ? (
              <img
                src={previewUrl}
                alt={file.name}
                onError={() => setPreviewError(true)}
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <span
                className={
                  'h-14 w-14 rounded-md border flex items-center justify-center ' +
                  TONE_BG[meta.tone]
                }
              >
                <Icon size={28} />
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="flex-1"
              onClick={() => onDownload?.(file.id)}
            >
              <Download size={14} /> Download
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="flex-1"
              onClick={() => onShare?.(file.id)}
            >
              <Copy size={14} /> Copy Link
            </Button>
          </div>
          <DetailRow label="Type" value={meta.label} />
          <DetailRow label="Size" value={fmtSize(file.size)} />
          <DetailRow label="Owner" value={file.ownerName} />
          <DetailRow label="Folder" value={file.folderName} />
          <DetailRow label="Version" value={`v${file.version}`} />
          <DetailRow label="Updated" value={formatDateTime(file.updatedAt)} />
          {file.tags?.length > 0 ? (
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
                Tags
              </p>
              <div className="flex flex-wrap gap-1">
                {file.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2 py-0.5 rounded text-xs font-medium bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* Version history — shown once a file has more than one
           *  version (a re-upload of the same name in the same location
           *  supersedes it as v2, v3…). Each version is downloadable; the
           *  newest is flagged "current". Single-version files omit this
           *  section (the "Version" row above already reads v1). */}
          {file.versions?.length > 1 ? (
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
                Version history
              </p>
              <ul className="space-y-1.5">
                {file.versions.map((v) => (
                  <li
                    key={v.version}
                    className="flex items-center justify-between gap-2 text-xs"
                  >
                    <span className="min-w-0 truncate text-[color:var(--color-ink-muted)]">
                      <span className="font-medium text-[color:var(--color-ink-strong)]">
                        v{v.version}
                      </span>
                      {v.version === file.version ? (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] text-[10px] font-medium">
                          current
                        </span>
                      ) : null}
                      {' · '}
                      {formatDateTime(v.uploadedAt)} · {fmtSize(v.sizeBytes)}
                      {v.uploadedByName ? ` · ${v.uploadedByName}` : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => downloadVersion(v.version)}
                      title={`Download v${v.version}`}
                      aria-label={`Download version ${v.version}`}
                      className="shrink-0 p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                    >
                      <Download size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Activity — only the upload event is shown because it's the
           *  one fact we actually have. The previous "3 people viewed in
           *  the last week" / "Downloaded 7 times" lines were hardcoded
           *  placeholders (no view/download tracking exists), so they
           *  showed the same fake numbers on every file — a tester
           *  reported a brand-new file claiming 3 views. Re-add real
           *  counts here once the backend records view / download
           *  events. */}
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
              Activity
            </p>
            <ul className="space-y-2 text-xs text-[color:var(--color-ink-muted)]">
              <li>
                <span className="font-medium text-[color:var(--color-ink)]">
                  {file.ownerName}
                </span>{' '}
                uploaded this file · {formatDateTime(file.updatedAt)}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-sm">
      <span className="text-[color:var(--color-ink-muted)]">{label}</span>
      <span className="text-[color:var(--color-ink-strong)] font-medium text-right">
        {value}
      </span>
    </div>
  )
}

function UploadFileModal({
  open,
  folders = [],
  defaultFolderId = null,
  uploading,
  progress = null,
  onClose,
  onUploaded,
}) {
  const [picked, setPicked] = useState(null)
  const [folderId, setFolderId] = useState('')
  /** Per-upload visibility — 'public' (everyone in the tenant can
   *  see / download) or 'private' (owner + company_admin only).
   *  Defaults to 'public' so the long-standing share-by-default
   *  behaviour holds unless the user toggles. Resets on close. */
  const [visibility, setVisibility] = useState('public')
  // Optional comma-separated tags applied at upload time. Resets on close.
  const [tags, setTags] = useState('')
  // Effective per-file cap (real operator-configured limit, not the ceiling).
  const maxUploadBytes = useMaxUploadBytes()
  // Sync the default folder (if the page has one selected) or fall
  // back to the first folder in the list. Resets to empty when the
  // modal closes. Effect-driven so we never call setState during the
  // render pass (React warns + double-renders otherwise).
  useEffect(() => {
    if (!open) {
      setFolderId('')
      setPicked(null)
      setVisibility('public')
      setTags('')
      return
    }
    if (folderId !== '') return
    if (defaultFolderId && folders.some((f) => f.id === defaultFolderId)) {
      setFolderId(defaultFolderId)
    } else if (folders.length > 0) {
      setFolderId(folders[0].id)
    }
    // `folderId` intentionally omitted — we only want to seed once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultFolderId, folders])

  const noFolders = folders.length === 0
  const { error: tagError } = parseTags(tags)
  const canSubmit = Boolean(picked && folderId && !uploading && !tagError)

  function onPick(file) {
    if (!file) return
    // Client-side pre-check — same allowlist as the server, surfaced as
    // a toast so the admin sees the rejection instantly instead of
    // waiting for an HTTP round trip. The server still validates on
    // its own, so a forged client can't bypass this.
    if (!isAllowedUploadFile(file)) {
      toast.error(
        `This file type is not supported. Allowed extensions: ${UPLOAD_EXTENSIONS_TEXT}.`,
      )
      return
    }
    // Size pre-check — reject over-cap files before the upload starts, using
    // the REAL server cap so we never stream a file Multer would abort.
    if (file.size > maxUploadBytes) {
      toast.error(
        `File is too large. Maximum size is ${Math.floor(maxUploadBytes / 1024 / 1024)} MB.`,
      )
      return
    }
    setPicked(file)
  }
  async function submit() {
    if (!canSubmit) return
    // Hand the raw browser File + folder + visibility + tags up to the
    // parent — it owns the multipart POST so we stay in one mutation
    // lifecycle.
    await onUploaded({ file: picked, folderId, visibility, tags })
    setPicked(null)
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload File"
      description="Pick a destination folder, then drag a file in or click to choose. Contact your platform admin about the current per-file size limit."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            <Upload size={14} />
            {uploading
              ? `Uploading…${typeof progress === 'number' ? ` ${progress}%` : ''}`
              : 'Upload'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Destination Folder
          </label>
          {noFolders ? (
            <div className="rounded-md border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] p-3 text-xs text-[color:var(--color-ink-strong)]">
              You need to create a folder first. Close this dialog and click{' '}
              <span className="font-semibold">New folder</span> above.
            </div>
          ) : (
            <select
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm cursor-pointer outline-none focus:border-[color:var(--color-accent)]"
            >
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <label
          className={
            'block rounded-lg border-2 border-dashed p-10 text-center ' +
            (noFolders
              ? 'border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] opacity-50 cursor-not-allowed'
              : 'border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] cursor-pointer hover:border-[color:var(--color-accent)]')
          }
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            if (noFolders) return
            onPick(e.dataTransfer.files?.[0])
          }}
        >
          <Upload
            size={24}
            className="mx-auto mb-2 text-[color:var(--color-accent-strong)]"
          />
          <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
            {picked ? picked.name : 'Click or drop a file here'}
          </p>
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            {picked
              ? uploading
                ? `${fmtSize(picked.size)} · ${
                    typeof progress === 'number' ? `${progress}% uploaded` : 'uploading…'
                  }`
                : `${fmtSize(picked.size)} · ready to upload`
              : `${UPLOAD_EXTENSIONS_TEXT} — up to 100 MB`}
          </p>
          {/* Live progress bar while the file streams — visible feedback
           *  so a large upload reads as "in progress", not "stuck". */}
          {uploading && typeof progress === 'number' ? (
            <div className="mt-3 h-1.5 w-full max-w-xs mx-auto rounded-full bg-[color:var(--color-rule)] overflow-hidden">
              <div
                className="h-full rounded-full bg-[color:var(--color-accent)] transition-[width] duration-150"
                style={{ width: `${progress}%` }}
              />
            </div>
          ) : null}
          <input
            type="file"
            className="hidden"
            disabled={noFolders}
            accept={UPLOAD_ACCEPT_ATTR}
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </label>

        <VisibilityPills value={visibility} onChange={setVisibility} kind="file" />

        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Tags <span className="font-normal lowercase tracking-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g. invoice, q1, finance"
            maxLength={500}
            disabled={noFolders}
            aria-invalid={Boolean(tagError)}
            className={
              'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] text-sm outline-none disabled:opacity-50 ' +
              (tagError
                ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
                : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
            }
          />
          {tagError ? (
            <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {tagError}
            </p>
          ) : (
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Comma-separated. Up to 20 tags, 32 characters each. You can also
              edit these later from the file’s menu.
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}


/** Shared zod schema for folder name. Mirrors the backend's 2..80
 *  bounds so an invalid name is caught client-side before we burn a
 *  round-trip — and the inline error message matches the toast the
 *  server would otherwise return. */
// Emoji / pictographic characters aren't allowed in folder names.
// `Extended_Pictographic` covers standard emoji (😀, ✅, …) and
// `Regional_Indicator` covers flag emoji (🇮🇳); plain digits/#/* are
// intentionally NOT matched (those use the broader `Emoji` property).
const EMOJI_RE = /[\p{Extended_Pictographic}\p{Regional_Indicator}]/u

const folderNameSchema = z.object({
  name: z
    .string({ message: 'Folder name is required.' })
    .trim()
    .min(1, 'Folder Name is required.')
    .min(2, 'Folder Name must be at least 2 characters.')
    .max(80, 'Folder Name must be at most 80 characters.')
    .refine((v) => !EMOJI_RE.test(v), {
      message: 'Folder name can’t contain emoji.',
    }),
})

function NewFolderModal({ open, saving, onClose, onCreate }) {
  // react-hook-form + zod surface validation INLINE under the field
  // (the SpaceCreateDialog pattern) — no global toast for "name too
  // short", and the Create button stays enabled instead of locking.
  const form = useForm({
    resolver: zodResolver(folderNameSchema),
    defaultValues: { name: '' },
  })
  // Reset on close so the next open starts blank. Effect-driven so
  // the setState call doesn't happen mid-render.
  useEffect(() => {
    if (!open) {
      form.reset({ name: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  async function submit(values) {
    try {
      await onCreate({ name: values.name.trim() })
    } catch (err) {
      const { message, fields } = extractApiError(err)
      if (fields?.name) {
        form.setError('name', { message: fields.name })
      } else {
        form.setError('name', { message })
      }
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Folder"
      description="Folders organise your files. Every uploaded file must live inside one."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-folder-form"
            loading={saving}
          >
            <FolderPlus size={14} />
            Create Folder
          </Button>
        </>
      }
    >
      <form
        id="new-folder-form"
        onSubmit={form.handleSubmit(submit)}
        noValidate
        className="space-y-4"
      >
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Folder name
            <span
              aria-hidden="true"
              className="text-[color:var(--color-priority-urgent)]"
            >
              {' *'}
            </span>
            <span className="sr-only">required</span>
          </label>
          <input
            {...form.register('name')}
            placeholder="Enter a Folder Name"
            className={
              'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] text-sm outline-none transition-colors ' +
              (form.formState.errors.name?.message
                ? 'border-[color:var(--color-priority-urgent)]'
                : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
            }
          />
          {form.formState.errors.name?.message ? (
            <p
              role="alert"
              className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
            >
              {form.formState.errors.name.message}
            </p>
          ) : null}
        </div>
      </form>
    </Modal>
  )
}

/**
 * Edit an existing folder — rename and re-describe. Wired to PATCH
 * `/file-folders/:id`. Delete lives on the folder's row menu, not in
 * here, so this dialog stays focused on the safe edit path.
 */
function EditFolderDialog({ folder, saving, onClose, onSave }) {
  const form = useForm({
    resolver: zodResolver(folderNameSchema),
    defaultValues: { name: '' },
  })
  // Seed fields when a folder lands in the dialog, and clear when the
  // caller closes it so the next open starts fresh. Keyed on the
  // folder id so switching directly between two folders re-seeds.
  useEffect(() => {
    if (folder) {
      form.reset({ name: folder.name ?? '' })
    } else {
      form.reset({ name: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder?.id])

  async function submit(values) {
    try {
      await onSave({ name: values.name.trim() })
    } catch (err) {
      const { message, fields } = extractApiError(err)
      if (fields?.name) {
        form.setError('name', { message: fields.name })
      } else {
        form.setError('name', { message })
      }
    }
  }
  return (
    <Modal
      open={Boolean(folder)}
      onClose={onClose}
      title="Edit Folder"
      description="Rename the folder. Files inside aren't moved."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-folder-form"
            loading={saving}
          >
            <Pencil size={14} />
            Save Changes
          </Button>
        </>
      }
    >
      <form
        id="edit-folder-form"
        onSubmit={form.handleSubmit(submit)}
        noValidate
        className="space-y-4"
      >
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Folder name
            <span
              aria-hidden="true"
              className="text-[color:var(--color-priority-urgent)]"
            >
              {' *'}
            </span>
            <span className="sr-only">required</span>
          </label>
          <input
            {...form.register('name')}
            placeholder="Enter a Folder Name"
            className={
              'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] text-sm outline-none transition-colors ' +
              (form.formState.errors.name?.message
                ? 'border-[color:var(--color-priority-urgent)]'
                : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
            }
          />
          {form.formState.errors.name?.message ? (
            <p
              role="alert"
              className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
            >
              {form.formState.errors.name.message}
            </p>
          ) : null}
        </div>
      </form>
    </Modal>
  )
}

function RenameFileDialog({ file, saving, onClose, onSave }) {
  const [name, setName] = useState('')
  // Sync internal state when a new file lands in the modal. Reset when
  // it closes so the next open starts clean. Keyed on file.id so
  // switching directly between two files re-seeds the input.
  useEffect(() => {
    if (file) setName(file.name ?? '')
    else setName('')
  }, [file?.id])
  function submit(e) {
    e.preventDefault()
    if (name.trim().length < 2) return
    onSave(name.trim())
  }
  return (
    <Modal
      open={file !== null}
      onClose={onClose}
      title="Rename File"
      description="Give this file a clearer name. The original is preserved on download."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            form="rename-file-form"
            type="submit"
            disabled={saving || name.trim().length < 2}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="rename-file-form" onSubmit={submit}>
        <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
          Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter a file name"
          autoFocus
          minLength={2}
          maxLength={255}
          className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm outline-none focus:border-[color:var(--color-accent)]"
        />
      </form>
    </Modal>
  )
}

/**
 * Move a file into a different Files & Links folder. Lists every folder
 * the user can see (`folders` — the same list rendered in the right-hand
 * Folders panel, including ones they created like "private Folder"). The
 * file's current folder is pre-selected and the "Move" button stays
 * disabled until a different folder is chosen, so there are no no-op
 * moves. The backend (PATCH /files/:id { folderId }) requires a folder,
 * so there is no "no folder" option here.
 */
function EditTagsDialog({ file, saving, onClose, onSave }) {
  const [draft, setDraft] = useState('')
  useEffect(() => {
    if (file) setDraft((file.tags ?? []).join(', '))
    else setDraft('')
  }, [file?.id])
  // "Add" vs "Edit" wording so an untagged file doesn't read as "Edit".
  const hasTags = (file?.tags?.length ?? 0) > 0
  const { tags: parsedTags, error: tagError } = parseTags(draft)
  // Live tag allowance. `tagCount` counts UNIQUE tags — the same basis as
  // the limit check (`parseTags` errors on `new Set(...).size > MAX_TAGS`
  // and the backend dedupes), so it matches what's enforced. `tagsLeft`
  // drives the "N tags left" counter, which counts DOWN toward 0 as tags
  // are added (clamped at 0 once the limit is reached/exceeded).
  const tagCount = new Set(parsedTags).size
  const tagsLeft = Math.max(0, MAX_TAGS - tagCount)
  // Disable Save when the parsed tags match what's already saved — a no-op,
  // mirroring the Edit Visibility dialog. This covers an untagged file with
  // an empty field (nothing to add), while STILL allowing an empty save when
  // the file HAS tags (clearing the field is the intended way to remove all
  // tags). Order-independent set equality — both sides are deduped/normalised.
  const currentTags = file?.tags ?? []
  const unchanged =
    parsedTags.length === currentTags.length &&
    parsedTags.every((t) => currentTags.includes(t))
  function submit(e) {
    e.preventDefault()
    if (tagError || unchanged) return
    onSave(parsedTags)
  }
  return (
    <Modal
      open={file !== null}
      onClose={onClose}
      title={hasTags ? 'Edit Tags' : 'Add Tags'}
      description="Comma-separated list. Tags are lower-cased and spaces become dashes."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            form="edit-tags-form"
            type="submit"
            disabled={saving || Boolean(tagError) || unchanged}
          >
            {saving ? 'Saving…' : 'Save Tags'}
          </Button>
        </>
      }
    >
      <form id="edit-tags-form" onSubmit={submit}>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
            Tags
          </label>
          {/* "N tags left" — counts DOWN toward 0 as tags are added, so the
           *  user watches their remaining allowance shrink. Turns red once
           *  the limit is exceeded, in step with the error + disabled Save
           *  below. */}
          <span
            className={
              'text-xs tabular-nums ' +
              (tagCount > MAX_TAGS
                ? 'text-[color:var(--color-priority-urgent)] font-semibold'
                : 'text-[color:var(--color-ink-muted)]')
            }
          >
            {tagsLeft} {tagsLeft === 1 ? 'tag' : 'tags'} left
          </span>
        </div>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
          placeholder="Enter the Tags"
          maxLength={500}
          aria-invalid={Boolean(tagError)}
          className={
            'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] text-sm outline-none ' +
            (tagError
              ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
              : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
          }
        />
        {tagError ? (
          <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
            {tagError}
          </p>
        ) : (
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            Up to 20 tags, 32 characters each.
          </p>
        )}
        {/* Reassure the user that tagging is reversible — they can always
         *  reopen this dialog to change or remove tags. (Replaces a
         *  misleading "cannot undo" notice a tester had requested.) */}
        <p className="mt-2 text-xs text-[color:var(--color-ink-subtle)]">
          You can edit or remove these tags anytime from the file’s ⋯ menu.
        </p>
      </form>
    </Modal>
  )
}

/**
 * Change a file's visibility (public ↔ private) after it's been
 * uploaded. Re-seeds from the file's current value each time a different
 * file lands in the dialog, and Save is disabled until the value
 * actually changes so a no-op PATCH isn't fired.
 */
// ─── External Links section ────────────────────────────────────────────────

/**
 * Renders the list of saved external links (Google Doc / Sheet / Slide,
 * Notion, Figma, generic URLs). Each row carries an Edit / Delete
 * inline action and an Open-in-new-tab affordance via the title link.
 *
 * Filtered by the page's `selectedFolderId` upstream — this component
 * just renders what it's given.
 */
function LinksSection({
  links,
  totalCount = links.length,
  dateFiltered = false,
  loading,
  error,
  selectedFolderId,
  currentUserId,
  canManageOthers = false,
  onEdit,
  onDelete,
}) {
  // `links` is already date-filtered by the parent. `totalCount` is the
  // pre-filter count so we can tell "no links exist at all" apart from
  // "links exist but the date filter hid them" — the two need different
  // empty-state copy.
  const filteredEmpty = links.length === 0 && totalCount > 0

  // Client-side pagination over the (already date-filtered) links, matching
  // the Files table's bar. `currentPage` is clamped so a folder / date
  // change that shrinks the list can't leave the view on an empty page.
  const [linksPage, setLinksPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [linksLimit, setLinksLimit] = usePageSize()
  const linksTotalPages = Math.max(1, Math.ceil(links.length / linksLimit))
  const currentLinksPage = Math.min(linksPage, linksTotalPages)
  const pagedLinks = links.slice(
    (currentLinksPage - 1) * linksLimit,
    currentLinksPage * linksLimit,
  )

  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm overflow-visible mt-6">
      <div className="px-6 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
            External Links
          </h2>
          <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5">
            Google Docs, Sheets, Slides, Notion, Figma — anywhere your team
            keeps work outside TaskMagix.
            {selectedFolderId ? ' (filtered by selected folder)' : ''}
          </p>
        </div>
        {/* Add-link action removed — links are added inside a Space and
         *  roll up here (read-only page). */}
      </div>
      {loading ? (
        <div className="p-10 text-center text-sm text-[color:var(--color-ink-muted)]">
          Loading links…
        </div>
      ) : error ? (
        <div className="p-10 text-center text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(error).message}
        </div>
      ) : links.length === 0 ? (
        <div className="p-10 text-center">
          <ExternalLinkIcon
            size={24}
            className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]"
          />
          {filteredEmpty ? (
            <>
              <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                No links match
              </p>
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                {dateFiltered
                  ? 'No links fall within the selected date range. Try widening it to “Any time”.'
                  : 'No links match the current filter.'}
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                No links yet
              </p>
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                Links attached to your tasks appear here.
              </p>
            </>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-[color:var(--color-rule)]">
          {pagedLinks.map((link) => {
            const meta = LINK_TYPE_META[link.type] ?? LINK_TYPE_META.generic
            const Icon = meta.icon
            const isOwner =
              currentUserId && link.createdById === currentUserId
            // Mirror the backend `assertCanMutate`: only the link's
            // creator (or a Company Admin) may edit/delete it. Hide the
            // icons otherwise so a Team Leader doesn't see actions that
            // would just 403 ("You can only edit links you saved.").
            const canManage = Boolean(isOwner) || canManageOthers
            return (
              <li
                key={link.id}
                className="px-6 py-3 flex items-center gap-3 hover:bg-[color:var(--color-card-hover)]"
              >
                <span
                  className={
                    'inline-flex h-9 w-9 items-center justify-center rounded-md border shrink-0 ' +
                    TONE_BG[meta.tone]
                  }
                >
                  <Icon size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-[color:var(--color-ink-strong)] hover:text-[color:var(--color-accent-strong)] truncate inline-flex items-center gap-1.5 max-w-full"
                    title={link.url}
                  >
                    <span className="truncate">{link.title}</span>
                    <ExternalLinkIcon
                      size={12}
                      className="text-[color:var(--color-ink-muted)] shrink-0"
                    />
                    {link.visibility === 'private' ? (
                      <span
                        title="Private — only you can see this link"
                        className="inline-flex items-center gap-0.5 shrink-0 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]"
                      >
                        Private
                      </span>
                    ) : (
                      <span
                        title="Public — anyone who can access its Space (your department, plus company-wide members and admins) can see this link"
                        className="inline-flex items-center gap-0.5 shrink-0 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]"
                      >
                        Public
                      </span>
                    )}
                  </a>
                  <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
                    <span className="font-medium">{meta.label}</span>
                    {link.domain ? <> · {link.domain}</> : null}
                    {' · '}
                    {link.ownerName}
                    {isOwner ? ' (you)' : ''}
                    {' · '}
                    {formatDateTime(link.updatedAt ?? link.createdAt)}
                  </p>
                  {link.tasks?.length > 0 ? (
                    <p
                      className="mt-0.5 flex items-center gap-1 text-xs text-[color:var(--color-ink-muted)] truncate"
                      title={`Attached to task: ${link.tasks
                        .map((t) => t.name)
                        .join(', ')}`}
                    >
                      <CornerDownRight
                        size={11}
                        className="shrink-0"
                        aria-hidden="true"
                      />
                      <span className="truncate">
                        {link.tasks.map((t) => t.name).join(', ')}
                      </span>
                    </p>
                  ) : null}
                </div>
                {canManage ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => onEdit(link)}
                      aria-label="Edit link"
                      title="Edit link"
                      className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(link.id)}
                      aria-label="Delete link"
                      title="Delete link"
                      className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
      {links.length > 0 ? (
        <Pagination
          page={currentLinksPage}
          totalPages={linksTotalPages}
          total={links.length}
          limit={linksLimit}
          onPageChange={setLinksPage}
          onLimitChange={(n) => {
            setLinksLimit(n)
            setLinksPage(1)
          }}
        />
      ) : null}
    </section>
  )
}

/**
 * Add a new external link. Title + URL are required; folder is
 * optional (links can be tenant-global if the user prefers).
 */
function AddLinkDialog({
  open,
  saving,
  folders,
  defaultFolderId,
  onClose,
  onSave,
}) {
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [folderId, setFolderId] = useState('')
  const [visibility, setVisibility] = useState('public')

  useEffect(() => {
    if (!open) {
      setTitle('')
      setUrl('')
      setFolderId('')
      setVisibility('public')
      return
    }
    // Seed folder to whatever the page is currently filtered to (if
    // that folder still exists), otherwise leave unfiled.
    if (
      defaultFolderId &&
      folders.some((f) => f.id === defaultFolderId)
    ) {
      setFolderId(defaultFolderId)
    }
  }, [open, defaultFolderId, folders])

  // Show the URL-format error only once the user has typed something, so
  // an empty field doesn't read as an error before they start.
  const urlInvalid = url.trim().length > 0 && !isLikelyValidUrl(url)
  // Title must be real text: ≥2 chars, at least one letter (rejects numbers- or
  // symbols-only), and no emoji — matching the Folder Name rule.
  const titleInvalid =
    title.trim().length > 0 &&
    (title.trim().length < 2 || EMOJI_RE.test(title) || !/\p{L}/u.test(title))
  const canSubmit =
    title.trim().length >= 2 &&
    !EMOJI_RE.test(title) &&
    /\p{L}/u.test(title) &&
    isLikelyValidUrl(url) &&
    !saving

  function submit() {
    if (!canSubmit) return
    onSave({
      title: title.trim(),
      url: url.trim(),
      folderId: folderId || undefined,
      visibility,
    })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add External Link"
      description="Save a Google Doc, Sheet, Slide, Notion page, Figma file or any other URL."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            <Link2 size={14} />
            {saving ? 'Saving…' : 'Save Link'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Title<span aria-hidden="true" className="ml-0.5 text-[color:var(--color-priority-urgent)]">*</span>
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            minLength={2}
            maxLength={120}
            placeholder="Enter a Title"
            aria-invalid={titleInvalid || undefined}
            className={
              'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] text-sm outline-none focus:border-[color:var(--color-accent)] ' +
              (titleInvalid ? 'border-[color:var(--color-priority-urgent)]' : 'border-[color:var(--color-rule)]')
            }
          />
          {titleInvalid ? (
            <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              Title must contain at least one letter and no emojis.
            </p>
          ) : null}
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            URL<span aria-hidden="true" className="ml-0.5 text-[color:var(--color-priority-urgent)]">*</span>
          </label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            type="url"
            placeholder="https://docs.google.com/document/d/"
            aria-invalid={urlInvalid}
            className={
              'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] text-sm outline-none ' +
              (urlInvalid
                ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
                : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
            }
          />
          {urlInvalid ? (
            <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              That doesn’t look like a valid URL. Enter a full link such as
              https://example.com.
            </p>
          ) : (
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              TaskMagix auto-detects Google Docs / Sheets / Slides, Notion,
              Figma and more — no extra setup needed.
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Folder (Optional)
          </label>
          <select
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm cursor-pointer outline-none focus:border-[color:var(--color-accent)]"
          >
            <option value="">— No folder —</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        <VisibilityPills
          value={visibility}
          onChange={setVisibility}
          kind="link"
          privateNote="Only you can see this link — a private link has no admin override."
        />
      </div>
    </Modal>
  )
}

/**
 * Edit a saved link — same fields as Add, plus the folder picker.
 * Re-seeds when a different link lands in the dialog (keyed on id).
 */
function EditLinkDialog({ link, saving, folders, onClose, onSave }) {
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [folderId, setFolderId] = useState('')
  const [visibility, setVisibility] = useState('public')

  useEffect(() => {
    if (link) {
      setTitle(link.title ?? '')
      setUrl(link.url ?? '')
      setFolderId(link.folderId ?? '')
      setVisibility(link.visibility ?? 'public')
    } else {
      setTitle('')
      setUrl('')
      setFolderId('')
      setVisibility('public')
    }
  }, [link?.id])

  const urlInvalid = url.trim().length > 0 && !isLikelyValidUrl(url)
  const titleInvalid =
    title.trim().length > 0 &&
    (title.trim().length < 2 || EMOJI_RE.test(title) || !/\p{L}/u.test(title))
  const canSubmit =
    title.trim().length >= 2 &&
    !EMOJI_RE.test(title) &&
    /\p{L}/u.test(title) &&
    isLikelyValidUrl(url) &&
    !saving

  function submit() {
    if (!canSubmit) return
    onSave({
      title: title.trim(),
      url: url.trim(),
      folderId: folderId || null,
      visibility,
    })
  }

  return (
    <Modal
      open={Boolean(link)}
      onClose={onClose}
      title="Edit Link"
      description="Update the title, URL or folder for this saved link."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            <Pencil size={14} />
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Title<span aria-hidden="true" className="ml-0.5 text-[color:var(--color-priority-urgent)]">*</span>
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            minLength={2}
            maxLength={120}
            className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm outline-none focus:border-[color:var(--color-accent)]"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            URL<span aria-hidden="true" className="ml-0.5 text-[color:var(--color-priority-urgent)]">*</span>
          </label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            type="url"
            aria-invalid={urlInvalid}
            className={
              'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] text-sm outline-none ' +
              (urlInvalid
                ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
                : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
            }
          />
          {urlInvalid ? (
            <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              That doesn’t look like a valid URL. Enter a full link such as
              https://example.com.
            </p>
          ) : null}
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Folder
          </label>
          <select
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm cursor-pointer outline-none focus:border-[color:var(--color-accent)]"
          >
            <option value="">— No folder —</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        <VisibilityPills
          value={visibility}
          onChange={setVisibility}
          kind="link"
          privateNote="Only you can see this link — a private link has no admin override."
        />
      </div>
    </Modal>
  )
}

/** Reusable Public / Private pill toggle. Used by the file upload
 *  dialog and the add / edit link dialogs so the affordance reads
 *  identically across surfaces. `kind` only changes the helper text
 *  below the pills ("file" vs "link"). */
/**
 * Clickable, sortable column header.
 *
 * Clicking toggles the direction on the active column; clicking a new
 * column starts at `defaultDir` — ascending for text (A→Z reads
 * naturally), descending for Size and Updated where "biggest"/"newest"
 * first is what people actually want.
 *
 * `sort` is the `<key>-<dir>` string owned by FilesPage; every key used
 * here must have a matching case in the `filteredFiles` comparator.
 * Renders a real <button> (keyboard-operable) and sets `aria-sort` on
 * the <th> so screen readers announce the current order.
 */
function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
  defaultDir = 'asc',
  className = '',
}) {
  const [activeKey, activeDir] = String(sort).split('-')
  const isActive = activeKey === sortKey
  const nextDir = isActive
    ? activeDir === 'asc'
      ? 'desc'
      : 'asc'
    : defaultDir
  const Icon = isActive ? (activeDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <th
      scope="col"
      aria-sort={
        isActive
          ? activeDir === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
      className={`px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)] ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(`${sortKey}-${nextDir}`)}
        title={`Sort by ${label} (${nextDir === 'asc' ? 'ascending' : 'descending'})`}
        className={
          // Active column keeps its directional arrow (below) to show the sort,
          // but NOT a bolder weight — so headers read consistently and the
          // default "Updated" sort doesn't stand out as bold on first paint.
          'group inline-flex items-center gap-1 cursor-pointer transition-colors hover:text-[color:var(--color-ink)] ' +
          (isActive ? 'text-[color:var(--color-ink)]' : '')
        }
      >
        {label}
        <Icon
          size={12}
          className={
            isActive
              ? 'text-[color:var(--color-accent)]'
              : 'opacity-0 group-hover:opacity-60 transition-opacity'
          }
          aria-hidden="true"
        />
      </button>
    </th>
  )
}

// VisibilityPills moved to components/VisibilityPills.jsx so the Space "Files"
// tab can reuse the exact same control + copy (imported at the top).
